import SalaryAccountStep from "../components/SalaryAccountStep";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useOutletContext } from "react-router-dom";
import "./ApplyLoanPage.css";
import LocationGate from "../components/LocationGate";
import {
  selectJourneyCheckpoint,
  advanceCompletedVerificationStages,
} from "../utils/journeyCheckpoint";

const PHONE_MISMATCH_MESSAGE =
  "Mobile number mismatch: the mobile number linked to your Aadhaar (Digilocker) does not match your registered mobile number.";
const AADHAAR_MISMATCH_MESSAGE =
  "Aadhaar mismatch: the Aadhaar verified on Digilocker does not match the Aadhaar linked to your PAN.";

const API = (process.env.REACT_APP_API_URL || "").trim().replace(/\/$/, "");
const configuredLoanAmountMin = Number(process.env.REACT_APP_LOAN_AMOUNT_MIN);
const configuredLoanAmountMax = Number(process.env.REACT_APP_LOAN_AMOUNT_MAX);
const LOAN_AMOUNT_MIN =
  Number.isSafeInteger(configuredLoanAmountMin) && configuredLoanAmountMin > 0
    ? configuredLoanAmountMin
    : 5000;
const LOAN_AMOUNT_MAX =
  Number.isSafeInteger(configuredLoanAmountMax) &&
  configuredLoanAmountMax >= LOAN_AMOUNT_MIN
    ? configuredLoanAmountMax
    : 100000;
// Step 0 opens with the minimum (₹5,000 unless configured) prefilled.
const DEFAULT_LOAN_AMOUNT = String(LOAN_AMOUNT_MIN);

// Top-right toast for step alerts. Renders into a shared fixed stack so
// several messages line up instead of overlapping. Closing only hides the
// toast until its message changes; it never alters step state.
function getToastStack() {
  let stack = document.getElementById("alp-toast-stack");
  if (!stack) {
    stack = document.createElement("div");
    stack.id = "alp-toast-stack";
    stack.className = "alp-toast-stack";
    document.body.appendChild(stack);
  }
  return stack;
}

const TOAST_ICONS = { error: "⚠️", warning: "⏳", success: "✅", info: "ℹ️" };

function AlertToast({ tone = "error", title, message }) {
  const [open, setOpen] = useState(true);
  useEffect(() => {
    setOpen(true);
  }, [message]);
  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div
      className={`alp-toast alp-toast-${tone}`}
      role={tone === "error" ? "alert" : "status"}
    >
      <span className="alp-toast-icon" aria-hidden="true">
        {TOAST_ICONS[tone]}
      </span>
      <div className="alp-toast-body">
        {title && <strong>{title}</strong>}
        <span>{message}</span>
      </div>
      <button
        type="button"
        className="alp-toast-close"
        aria-label="Dismiss message"
        onClick={() => setOpen(false)}
      >
        ×
      </button>
      <span className="alp-toast-bar" aria-hidden="true" />
    </div>,
    getToastStack(),
  );
}

// Employment first; selfie follows address proof at step 5.
const STEPS = [
  {
    id: 0,
    key: "employment",
    title: "Employment Info",
    icon: "💼",
    color: "#0891b2",
    desc: "Tell us about your income sources.",
  },
  {
    id: 1,
    key: "aa",
    title: "Account Aggregator",
    icon: "🏦",
    color: "#147df5",
    desc: "Securely link bank statements before continuing your verification.",
  },
  {
    id: 2,
    key: "digilocker",
    title: "Digilocker KYC",
    icon: "📜",
    color: "#38bdf8",
    desc: "Complete your Aadhaar verification.",
  },
  {
    id: 3,
    key: "address_proof",
    title: "Address Proof",
    icon: "🏠",
    color: "#0284c7",
    desc: "Upload Address proof for verification.",
  },
  {
    id: 4,
    key: "selfie",
    title: "Selfie Verification",
    icon: "📸",
    color: "#e11d48",
    desc: "Complete liveness and face match with Aadhaar in one quick step.",
  },
  {
    id: 5,
    key: "relation",
    title: "References",
    icon: "👥",
    color: "#147df5",
    desc: "Please provide details of two people we can contact as references.",
  },
  {
    id: 6,
    key: "salary_account",
    title: "Salary Account",
    icon: "🏦",
    color: "#147df5",
    desc: "Choose the linked account where you receive your salary.",
  },
];

// The applicant fills steps 0..SUBMIT_STEP and submits from SUBMIT_STEP.
// Approved applications continue in the dedicated Sanction Letter dashboard tab.
const SUBMIT_STEP = STEPS.findIndex((s) => s.key === "salary_account");

const EMP_TYPES = ["Salaried", "Self-Employed"];
const EMPLOYMENT_OPTIONS = {
  Salaried: {
    icon: "💼",
    tone: "cyan",
    label: "Salaried",
    desc: "Fixed monthly salary from a company",
  },
  "Self-Employed": {
    icon: "🛠️",
    tone: "green",
    label: "Self-Employed",
    desc: "Business, freelance or consulting income",
  },
};

const EMPLOYMENT_FIELDS = {
  Salaried: [
    {
      key: "employer",
      label: "Company Name",
      placeholder: "e.g. Tata Consultancy Services",
      icon: "🏢",
    },
    {
      key: "designation",
      label: "Designation",
      placeholder: "e.g. Senior Manager",
      icon: "👔",
    },
    {
      key: "income",
      label: "Monthly Net Salary (₹)",
      placeholder: "75000",
      type: "number",
      icon: "💰",
    },
    {
      key: "salary_date",
      label: "Salary Date (1-31)",
      placeholder: "e.g. 5",
      type: "number",
      icon: "📅",
    },
    // {
    //   key: "epfo_number",
    //   label: "EPFO Number (Optional)",
    //   placeholder: "e.g. MHBAN00000000000000000",
    //   type: "text",
    //   icon: "🏛️",
    //   required: false,
    // },
    {
      key: "personal_email",
      label: "Personal Email Address",
      placeholder: "e.g. you@gmail.com",
      type: "email",
      icon: "✉️",
    },
    {
      key: "email",
      label: "Company Email Address",
      placeholder: "e.g. you@company.com",
      type: "email",
      icon: "📧",
      required: false,
    },
    {
      key: "amount",
      label: "Loan Amount (₹)",
      placeholder: "Enter the amount you need",
      type: "number",
      icon: "💰",
    },
  ],
  "Self-Employed": [
    {
      key: "employer",
      label: "Business Name",
      placeholder: "e.g. Blue Peak Traders",
      icon: "🏪",
    },
    {
      key: "businessType",
      label: "Profession/Trade",
      placeholder: "e.g. Consultant, Architect",
      icon: "🛠️",
    },
    {
      key: "income",
      label: "Monthly Income (₹)",
      placeholder: "80000",
      type: "number",
      icon: "💰",
    },
    {
      key: "salary_date",
      label: "Income Receipt Date (1-31)",
      placeholder: "e.g. 10",
      type: "number",
      icon: "📅",
      required: false,
    },
    // {
    //   key: "epfo_number",
    //   label: "EPFO Number (Optional)",
    //   placeholder: "e.g. MHBAN00000000000000000",
    //   type: "text",
    //   icon: "🏛️",
    //   required: false,
    // },
    {
      key: "personal_email",
      label: "Personal Email Address",
      placeholder: "e.g. you@gmail.com",
      type: "email",
      icon: "✉️",
    },
    {
      key: "email",
      label: "Company Email Address",
      placeholder: "e.g. you@company.com",
      type: "email",
      icon: "📧",
      required: false,
    },
    {
      key: "amount",
      label: "Loan Amount (₹)",
      placeholder: "Enter the amount you need",
      type: "number",
      icon: "💰",
    },
  ],
};

const REFERENCE_RELATIONS = [
  "Father",
  "Mother",
  "Spouse",
  "Brother",
  "Sister",
  "Son",
  "Daughter",
  "Friend",
  "Colleague",
  "Relative",
  "Other",
];

const UNIQUE_REFERENCE_RELATIONS = ["Father", "Mother", "Spouse"];

const REFERENCE_SLOTS = [
  { n: 1, namePlaceholder: "e.g. REFE1", phonePlaceholder: "e.g. 9876543210" },
  { n: 2, namePlaceholder: "e.g. REFE2", phonePlaceholder: "e.g. 9876543210" },
];

// Free-text relation, only required when 'Other' is picked.
const CUSTOM_RELATION_RE = /^[A-Za-z][A-Za-z\s.'-]{1,49}$/;

// Reference names are letters only: no digits, so a phone number can't be
// typed into the name field.
const REFERENCE_NAME_RE = /^[A-Za-z][A-Za-z\s.'-]{0,49}$/;

const INDIAN_MOBILE_RE = /^[6-9]\d{9}$/;

// Strips +91 / 0 prefixes so a stored "+919876543210" compares equal to "9876543210".
function toIndianMobile(value) {
  let digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
  if (digits.length === 11 && digits.startsWith("0")) digits = digits.slice(1);
  return digits;
}

function hexToRgb(hex) {
  const value = String(hex || "")
    .replace("#", "")
    .trim();
  if (!/^[0-9a-fA-F]{6}$/.test(value)) {
    return "37, 99, 235";
  }

  const int = parseInt(value, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `${r}, ${g}, ${b}`;
}

function useReusableDocument({
  userId,
  token,
  prefix,
  enabled = true,
  onChange,
  setMessage,
}) {
  const [checking, setChecking] = useState(enabled);
  useEffect(() => {
    if (!enabled) {
      setChecking(false);
      return undefined;
    }
    let cancelled = false;
    const controller = new AbortController();
    setChecking(true);
    onChange(`${prefix}Done`, false);
    onChange(`${prefix}DocumentId`, null);
    onChange(`${prefix}NotRequired`, false);
    setMessage("Checking previously uploaded documents…");
    const load = async () => {
      try {
        if (!userId || !token)
          throw new Error("Please sign in again to check your documents.");
        // Lives in the one-row-per-user user_documents table.
        const response = await fetch(`${API}/api/profile/address-proof`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        const result = await response.json();
        if (!response.ok || !result.success)
          throw new Error(
            "Unable to check saved documents. Please upload a fresh copy.",
          );
        if (cancelled) return;
        if (result.required === false) {
          onChange(`${prefix}NotRequired`, true);
          onChange(`${prefix}Done`, true);
          setMessage("");
          return;
        }
        const document =
          result.reusable === true
            ? {
                id: result.documentId,
                file_name: result.fileName,
                uploaded_at: result.uploadedAt,
              }
            : null;
        if (document) {
          onChange(`${prefix}DocumentId`, document.id);
          onChange(`${prefix}FileName`, document.file_name);
          onChange(`${prefix}UploadedAt`, document.uploaded_at);
          onChange(`${prefix}Done`, true);
          setMessage(
            "We already have this document on your application. You can continue or upload a replacement.",
          );
        } else {
          setMessage(
            "No document is saved on this application yet. Please upload one to continue.",
          );
        }
      } catch (error) {
        if (!cancelled) setMessage(error.message);
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [userId, token, prefix, enabled, onChange, setMessage]);
  return checking;
}

// Oval guide in the selfie preview's 100 x 133 SVG viewBox.
const SELFIE_OVAL = { cx: 50, cy: 55, rx: 32, ry: 42 };
const FACE_DETECTOR_WASM =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.1.1/wasm";
const FACE_DETECTOR_MODEL =
  "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite";
// How long the face must stay centred before "I am ready" triggers itself.
const SELFIE_AUTO_READY_MS = 2000;
const SELFIE_HINTS = {
  none: "Align face in the oval",
  multiple: "Only one face, please",
  "off-center": "Center your face in the oval",
  far: "Move a little closer",
  close: "Move a little back",
  aligned: "Hold still…",
};

// Loaded once per page and shared by every mount; a failed load can be retried.
let faceDetectorPromise = null;
function loadFaceDetector() {
  if (!faceDetectorPromise) {
    faceDetectorPromise = import("@mediapipe/tasks-vision")
      .then(async ({ FaceDetector, FilesetResolver }) => {
        const vision = await FilesetResolver.forVisionTasks(FACE_DETECTOR_WASM);
        return FaceDetector.createFromOptions(vision, {
          baseOptions: { modelAssetPath: FACE_DETECTOR_MODEL, delegate: "CPU" },
          runningMode: "VIDEO",
          minDetectionConfidence: 0.6,
        });
      })
      .catch((err) => {
        faceDetectorPromise = null;
        throw err;
      });
  }
  return faceDetectorPromise;
}

// Maps face boxes from video pixels into the mirrored, object-fit: cover preview
// and checks the face sits centred in the oval at a sensible size.
export function assessSelfieFace(detections, video, frame) {
  if (!detections?.length) return "none";
  if (detections.length > 1) return "multiple";
  const box = detections[0].boundingBox;
  if (!box || !video.width || !video.height || !frame.width || !frame.height)
    return "none";
  const scale = Math.max(frame.width / video.width, frame.height / video.height);
  const offsetX = (video.width * scale - frame.width) / 2;
  const offsetY = (video.height * scale - frame.height) / 2;
  const centerX =
    frame.width - ((box.originX + box.width / 2) * scale - offsetX);
  const centerY = (box.originY + box.height / 2) * scale - offsetY;
  const x = (centerX / frame.width) * 100;
  const y = (centerY / frame.height) * 133;
  const widthRatio =
    ((box.width * scale) / frame.width) * 100 / (SELFIE_OVAL.rx * 2);
  // The detector's box runs brow to chin, so a face filling the oval puts the
  // box centre (around the nose) about a quarter of ry below the oval centre.
  const dx = (x - SELFIE_OVAL.cx) / SELFIE_OVAL.rx;
  const dy = (y - (SELFIE_OVAL.cy + SELFIE_OVAL.ry * 0.25)) / SELFIE_OVAL.ry;
  if (dx * dx + dy * dy > 0.35 * 0.35) return "off-center";
  if (widthRatio < 0.25) return "far";
  if (widthRatio > 1.1) return "close";
  return "aligned";
}

export function StepSelfieCapture({ data, onChange }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const previewFrameRef = useRef(null);
  // Guards against re-POSTing the same image on re-render / StrictMode double-mount.
  const selfieUploadRef = useRef("");
  const [cameraReady, setCameraReady] = useState(false);
  const [captured, setCaptured] = useState(false);
  const [preview, setPreview] = useState("");
  const [checkingSelfie, setCheckingSelfie] = useState(true);
  const [selfieMessage, setSelfieMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // 3-2-1 before capture; null when not counting. Starts once the face holds in
  // the oval, or from "I am ready" when face detection is unavailable.
  const [countdown, setCountdown] = useState(null);
  const [faceHint, setFaceHint] = useState("none");
  const countdownRef = useRef(null);
  const manualCountdownRef = useRef(false);
  const captureRef = useRef(null);
  countdownRef.current = countdown;
  const counting = countdown !== null;
  const faceAligned = counting || faceHint === "aligned";
  const [photoSaveError, setPhotoSaveError] = useState("");
  const [verifyingSelfie, setVerifyingSelfie] = useState(false);

  const session = JSON.parse(localStorage.getItem("userSession") || "{}");
  const userId = session?.userId;
  const token = session?.token || "";

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    onChange("selfieDone", false);
    setCheckingSelfie(true);
    const load = async () => {
      try {
        const response = await fetch(`${API}/api/profile/selfie`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        const result = await response.json();
        if (cancelled) return;
        if (!response.ok || !result.success)
          throw new Error(
            "Unable to check your saved selfie. Please take a new selfie.",
          );
        if (result.reusable === true && result.imageUrl) {
          selfieUploadRef.current = result.imageUrl;
          setPreview(result.imageUrl);
          setCaptured(true);
          onChange("selfieImage", result.imageUrl);
          onChange("selfieCapturedAt", result.uploadedAt);
          onChange("selfieUploaded", true);
          onChange("selfieDone", true);
          setSelfieMessage(
            "We already have your selfie on this application. You can continue or retake it.",
          );
        } else {
          setSelfieMessage(
            "No selfie is saved on this application yet. Please take one to continue.",
          );
        }
      } catch (error) {
        if (!cancelled)
          setSelfieMessage(
            "Unable to check your saved selfie. Please take a new selfie.",
          );
      } finally {
        if (!cancelled) setCheckingSelfie(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token, onChange]);

  const startCamera = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 720 },
          height: { ideal: 960 },
          aspectRatio: { ideal: 0.75 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraReady(true);
    } catch (err) {
      setError("Camera access is required to take a selfie.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (captured || checkingSelfie) return undefined;
    startCamera();
    return () => stopCamera();
  }, [captured, checkingSelfie, startCamera, stopCamera]);

  const captureSelfie = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const sourceWidth = video.videoWidth || 720;
    const sourceHeight = video.videoHeight || 720;
    const frameRect = previewFrameRef.current?.getBoundingClientRect();
    const frameAspectRatio =
      frameRect?.width && frameRect?.height
        ? frameRect.width / frameRect.height
        : sourceWidth / sourceHeight;

    let cropWidth = sourceWidth;
    let cropHeight = sourceHeight;
    let cropX = 0;
    let cropY = 0;

    // Match the saved selfie to the same framing shown in the live preview.
    if (sourceWidth / sourceHeight > frameAspectRatio) {
      cropWidth = sourceHeight * frameAspectRatio;
      cropX = (sourceWidth - cropWidth) / 2;
    } else {
      cropHeight = sourceWidth / frameAspectRatio;
      cropY = (sourceHeight - cropHeight) / 2;
    }

    canvas.width = Math.round(cropWidth);
    canvas.height = Math.round(cropHeight);
    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(
      video,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      canvas.width,
      canvas.height,
    );
    ctx.restore();
    const image = canvas.toDataURL("image/jpeg", 0.92);
    setPreview(image);
    setCaptured(true);
    // Done only once the server matches it with the Aadhaar photo.
    onChange("selfieDone", false);
    onChange("selfieImage", image);
    onChange("selfieCapturedAt", new Date().toISOString());
    onChange("selfieUploaded", false);
    stopCamera();
  };

  // The server face-matches the selfie with the Aadhaar photo and stores it as the
  // profile photo only on a match. Anything else blocks the step until a retake.
  useEffect(() => {
    const image = data.selfieImage;
    if (!captured || !image || data.selfieUploaded || !userId) return;
    if (selfieUploadRef.current === image) return;
    selfieUploadRef.current = image;

    let active = true;
    setPhotoSaveError("");
    setVerifyingSelfie(true);
    (async () => {
      try {
        const response = await fetch(`${API}/api/profile/selfie`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ userId, image }),
        });
        const json = await response.json();
        if (!response.ok || !json.success)
          throw new Error(
            [
              json.message || "Could not save the selfie.",
              json.matchPercentage != null &&
                `Face match: ${json.matchPercentage}%.`,
            ]
              .filter(Boolean)
              .join(" "),
          );
        if (!active) return;
        setPhotoSaveError("");
        setSelfieMessage(
          json.matchPercentage != null
            ? `Your selfie matches your Aadhaar photo (${json.matchPercentage}% match).`
            : "Your selfie has been saved.",
        );
        onChange("selfieUploaded", true);
        onChange("selfieDone", true);
      } catch (err) {
        if (!active) return;
        // Keep selfieUploadRef set so this image is not re-sent; Retake clears it.
        onChange("selfieDone", false);
        setPhotoSaveError(
          err.message || "We could not verify your selfie. Please retake it.",
        );
      } finally {
        if (active) setVerifyingSelfie(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [
    captured,
    data.selfieImage,
    data.selfieUploaded,
    userId,
    token,
    onChange,
  ]);

  captureRef.current = captureSelfie;

  // Polls the live preview for a face. The oval turns green as soon as the face
  // is centred; holding it there for SELFIE_AUTO_READY_MS presses "I am ready".
  // A few misaligned frames cancel an automatic countdown.
  useEffect(() => {
    if (!cameraReady || captured) return undefined;
    let stopped = false;
    let timer = null;
    let streak = 0;
    let alignedSince = null;
    loadFaceDetector()
      .then((detector) => {
        if (stopped) return;
        timer = setInterval(() => {
          const video = videoRef.current;
          const frame = previewFrameRef.current?.getBoundingClientRect();
          if (!video || video.readyState < 2 || !frame) return;
          let hint = "none";
          try {
            const result = detector.detectForVideo(video, performance.now());
            hint = assessSelfieFace(
              result.detections,
              { width: video.videoWidth, height: video.videoHeight },
              frame,
            );
          } catch {
            return;
          }
          setFaceHint(hint);
          const now = performance.now();
          streak = hint === "aligned" ? Math.max(streak, 0) + 1 : Math.min(streak, 0) - 1;
          if (hint === "aligned") alignedSince ??= now;
          else if (streak <= -2) alignedSince = null;
          if (
            countdownRef.current === null &&
            alignedSince !== null &&
            now - alignedSince >= SELFIE_AUTO_READY_MS
          ) {
            alignedSince = null;
            manualCountdownRef.current = false;
            setCountdown(3);
          } else if (
            countdownRef.current !== null &&
            !manualCountdownRef.current &&
            streak <= -3
          ) {
            setCountdown(null);
          }
        }, 150);
      })
      .catch(() => {
        // No detector (offline / unsupported): "I am ready" still works.
      });
    return () => {
      stopped = true;
      clearInterval(timer);
      setFaceHint("none");
      setCountdown(null);
    };
  }, [cameraReady, captured]);

  useEffect(() => {
    if (countdown === null) return undefined;
    if (countdown === 0) {
      setCountdown(null);
      captureRef.current?.();
      return undefined;
    }
    const timer = setTimeout(() => setCountdown((c) => (c === null ? c : c - 1)), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const startLivenessCheck = () => {
    manualCountdownRef.current = true;
    setCountdown(3);
  };

  const retakeSelfie = async () => {
    setSelfieMessage("");
    setCaptured(false);
    setCountdown(null);
    setPreview("");
    setPhotoSaveError("");
    setVerifyingSelfie(false);
    selfieUploadRef.current = "";
    onChange("selfieDone", false);
    onChange("selfieImage", "");
    onChange("selfieCapturedAt", null);
    onChange("selfieUploaded", false);
  };

  return (
    <div className="alp-step-body">
      <div className="alp-selfie-card">
        {checkingSelfie ? (
          <p>Checking your saved selfie…</p>
        ) : (
          selfieMessage && <p>{selfieMessage}</p>
        )}
        {error && <AlertToast title="Camera" message={error} />}

        {verifyingSelfie && (
          <p>Verifying your selfie with your Aadhaar photo…</p>
        )}
        {photoSaveError && (
          <AlertToast title="Selfie verification" message={photoSaveError} />
        )}

        {!captured ? (
          <div style={{ display: "grid", gap: "1rem" }}>
            <div
              style={{
                position: "relative",
                width: "min(100%, 3in)",
                aspectRatio: "3 / 4",
                margin: "0 auto",
                borderRadius: "18px",
                overflow: "hidden",
                border: "1px solid #fbcfe8",
                background: "#0f172a",
              }}
              ref={previewFrameRef}
            >
              <video
                ref={videoRef}
                playsInline
                muted
                autoPlay
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                  transform: "scaleX(-1)",
                }}
              />
              {cameraReady && (
                <>
                  <svg
                    viewBox="0 0 100 133"
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      pointerEvents: "none",
                      zIndex: 10,
                    }}
                  >
                    <defs>
                      <mask id="oval-mask">
                        <rect width="100" height="133" fill="white" />
                        <ellipse cx="50" cy="55" rx="32" ry="42" fill="black" />
                      </mask>
                    </defs>
                    <rect
                      width="100"
                      height="133"
                      fill="rgba(0,0,0,0.65)"
                      mask="url(#oval-mask)"
                    />
                    <ellipse
                      cx="50"
                      cy="55"
                      rx="32"
                      ry="42"
                      fill="none"
                      stroke={faceAligned ? "#10b981" : "#f43f5e"}
                      strokeWidth={faceAligned ? "1.5" : "1"}
                      style={{ transition: "stroke 0.2s ease" }}
                    />
                  </svg>
                  {counting && countdown > 0 && (
                    <div
                      key={countdown}
                      aria-live="assertive"
                      style={{
                        position: "absolute",
                        top: `${(SELFIE_OVAL.cy / 133) * 100}%`,
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: "white",
                        fontSize: "4.5rem",
                        fontWeight: 800,
                        lineHeight: 1,
                        textShadow: "0 2px 12px rgba(0,0,0,0.7)",
                        zIndex: 25,
                        animation: "kyc-count 1s ease-out",
                      }}
                    >
                      {countdown}
                    </div>
                  )}
                  {counting && (
                    <div
                      style={{
                        position: "absolute",
                        top: "13%",
                        left: "18%",
                        width: "64%",
                        height: "2px",
                        background: "#10b981",
                        boxShadow: "0 0 8px 2px rgba(16, 185, 129, 0.6)",
                        zIndex: 20,
                        animation:
                          "kyc-scan 1.5s ease-in-out infinite alternate",
                      }}
                    />
                  )}
                  <div
                    style={{
                      position: "absolute",
                      bottom: "6%",
                      left: 0,
                      width: "100%",
                      textAlign: "center",
                      color: "white",
                      zIndex: 20,
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      textShadow: "0 1px 3px rgba(0,0,0,0.8)",
                    }}
                  >
                    {counting ? "Hold still…" : SELFIE_HINTS[faceHint]}
                  </div>
                </>
              )}
              {!cameraReady && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "grid",
                    placeItems: "center",
                    color: "white",
                    background: "rgba(15,23,42,0.35)",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                  }}
                >
                  {loading ? "Starting camera…" : "Camera preview loading…"}
                </div>
              )}
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "0.75rem",
                flexWrap: "wrap",
              }}
            >
              <button
                className="alp-btn-primary"
                onClick={startLivenessCheck}
                disabled={!cameraReady || counting}
                style={{ minWidth: "160px" }}
              >
                {counting ? `Capturing in ${countdown}…` : "I am ready"}
              </button>
              <button
                className="alp-btn-ghost"
                onClick={startCamera}
                disabled={loading || counting}
                style={{ minWidth: "140px" }}
              >
                {loading ? "Restarting…" : "Restart Camera"}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: "grid", gap: "1rem" }}>
            <div
              style={{
                width: "min(100%, 3in)",
                aspectRatio: "3 / 4",
                margin: "0 auto",
                borderRadius: "18px",
                overflow: "hidden",
                border: "1px solid #bbf7d0",
                background: "#f0fdf4",
              }}
            >
              <img
                src={preview}
                alt="Captured selfie"
                style={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                  objectFit: "cover",
                }}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "center" }}>
              <button
                className="alp-btn-ghost"
                onClick={retakeSelfie}
                style={{ minWidth: "140px" }}
              >
                Retake
              </button>
            </div>
          </div>
        )}

        <canvas ref={canvasRef} style={{ display: "none" }} />
        <style>{`
                    @keyframes kyc-scan {
                        from { top: 13%; }
                        to { top: 73%; }
                    }
                    @keyframes kyc-count {
                        from { opacity: 0; transform: translate(-50%, -50%) scale(1.6); }
                        to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
                    }
                `}</style>
      </div>
    </div>
  );
}

function AccountAggregatorPopup({ sessionInfo, onClose }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement;
    dialog.showModal();
    document.body.style.overflow = "hidden";
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  return createPortal(
    <dialog
      ref={dialogRef}
      className="alp-aa-embed"
      aria-label="Bank account consent"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div className="alp-aa-embed-header">
        <div className="alp-aa-embed-heading">
          <h2>Connect your bank</h2>
          <span>Complete consent to continue</span>
        </div>
        <button
          type="button"
          className="alp-aa-embed-close"
          onClick={onClose}
          autoFocus
        >
          <span aria-hidden="true">×</span> Close
        </button>
      </div>
      <div className="alp-aa-embed-content">
        <iframe
          className="alp-aa-embed-frame"
          title="Account Aggregator bank consent"
          src={sessionInfo.redirectUrl}
        />
      </div>
    </dialog>,
    document.body,
  );
}

export function StepAccountAggregator({ data, onChange, onComplete }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessionInfo, setSessionInfo] = useState(data.aaSession || null);
  const [consentReceived, setConsentReceived] = useState(false);
  const [requiresRerun, setRequiresRerun] = useState(false);
  const [showAggregator, setShowAggregator] = useState(false);
  const startingRef = useRef(false);
  // Revisiting an already completed consent must not navigate away from this step.
  const advanceOnApprovalRef = useRef(
    !data.aaDone &&
      String(data.aaSession?.consent_status || "").toUpperCase() !== "ACTIVE",
  );
  const [statusCheckVersion, setStatusCheckVersion] = useState(0);
  const [editingMobile, setEditingMobile] = useState(false);
  const [mobileDraft, setMobileDraft] = useState("");
  const [mobileError, setMobileError] = useState("");
  const session = JSON.parse(localStorage.getItem("userSession") || "{}");
  const userId = session?.userId;
  // aaMobile is the applicant's override; it is what the consent OTP is sent to.
  const profileMobile = toIndianMobile(session?.phone || data.mobile || "");
  const mobile = toIndianMobile(data.aaMobile) || profileMobile;
  const mobileValid = INDIAN_MOBILE_RE.test(mobile);
  const email = session?.email || data.email || "";
  const token = session?.token || "";
  const selectedAaMethod = "account_aggregator";

  // Recover the server-side consent even if the browser draft lost its ID.
  useEffect(() => {
    if (sessionInfo?.sessionId || !token || startingRef.current)
      return undefined;
    let cancelled = false;
    fetch(`${API}/api/signzy/checkAAStatus`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    })
      .then(async (response) => {
        const result = await response.json();
        if (cancelled || startingRef.current || response.status === 404) return;
        if (!response.ok || !result.success)
          throw new Error(result.message || "Unable to load saved consent");
        if (!result.requestId) return;
        advanceOnApprovalRef.current = false;
        const restored = {
          sessionId: result.requestId,
          consent_status: result.consentStatus,
          updatedAt: result.updatedAt,
        };
        setSessionInfo(restored);
        onChange("aaSession", restored);
      })
      .catch((error) => {
        if (!cancelled) setError(error.message);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionInfo?.sessionId, token, onChange]);

  const startAggregator = useCallback(async () => {
    if (startingRef.current) return;
    if (!userId || !token) {
      setError("Login session missing. Please sign in again.");

      onChange("aaStatus", "error");
      return;
    }

    // The provider sends the consent OTP to this number, so never fall back to a placeholder.
    if (!INDIAN_MOBILE_RE.test(mobile)) {
      setError(
        "Enter a valid 10-digit mobile number before starting Account Aggregator.",
      );

      onChange("aaStatus", "error");
      return;
    }

    setError("");
    setShowAggregator(false);
    startingRef.current = true;
    advanceOnApprovalRef.current = true;
    setLoading(true);
    setConsentReceived(false);
    setRequiresRerun(false);
    setSessionInfo(null);
    onChange("aaSession", null);
    onChange("aaDone", false);

    onChange("aaStatus", "starting");
    try {
      const response = await fetch(`${API}/api/signzy/consent-request`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ mobile, email }),
      });

      const json = await response.json();
      if (response.ok && json.success) {
        const signzyData = json.data || {};
        const nextSession = {
          sessionId: json.requestId || signzyData.requestId,
          consentHandle: json.requestId || signzyData.requestId,
          consentId: signzyData.consentId,
          redirectUrl: json.redirectUrl || signzyData.redirectUrl,
          consent_status: json.consentStatus || "PENDING",
        };
        if (
          !nextSession.sessionId ||
          !nextSession.redirectUrl ||
          String(json.status).toUpperCase() === "ERROR"
        ) {
          throw new Error(
            "The provider did not return a valid consent session. Please try again.",
          );
        }
        onChange("aaSession", nextSession);
        setSessionInfo(nextSession);

        setShowAggregator(true);

        onChange("aaMethod", "account_aggregator");
        onChange("aaStatus", "pending_consent");
        onChange("aaDone", false);
      } else {
        throw new Error(json.message || "Unable to start Account Aggregator");
      }
    } catch (err) {
      setShowAggregator(false);
      setError(err.message || "Unable to start Account Aggregator");

      onChange("aaStatus", "error");
    } finally {
      startingRef.current = false;
      setLoading(false);
    }
  }, [email, mobile, onChange, userId, token]);

  const saveMobile = () => {
    const next = toIndianMobile(mobileDraft);
    if (!INDIAN_MOBILE_RE.test(next)) {
      setMobileError("Enter a valid 10-digit mobile number.");
      return;
    }
    if (next !== mobile) {
      // Any consent already raised belongs to the previous number — discard it
      // so the next Start raises a fresh consent against the new one.
      onChange("aaSession", null);
      onChange("aaStatus", "idle");
      onChange("aaDone", false);
      setSessionInfo(null);
      setShowAggregator(false);

      setError("");
    }
    onChange("aaMobile", next);
    setEditingMobile(false);
    setMobileError("");
  };

  useEffect(() => {
    if (data.aaDone) {
      setSessionInfo(data.aaSession || null);
    }
  }, [data.aaDone, data.aaSession]);

  const sessionData = sessionInfo || data.aaSession || {};
  const consentStatus = String(sessionData?.consent_status || "").toUpperCase();
  const consentFailed =
    requiresRerun ||
    ["REJECTED", "REVOKED", "EXPIRED", "FAILED", "ERROR"].includes(
      consentStatus,
    );

  useEffect(() => {
    const refreshStatus = () => setStatusCheckVersion((version) => version + 1);
    window.addEventListener("focus", refreshStatus);
    return () => window.removeEventListener("focus", refreshStatus);
  }, []);

  // Always use the ID saved from consent creation, never an ID from the redirect.
  const consentRequestId = sessionInfo?.sessionId;
  useEffect(() => {
    onChange("aaDone", false);
    setConsentReceived(false);
    if (!consentRequestId || !token) return undefined;
    let cancelled = false;
    let timer;
    const controller = new AbortController();
    const checkStatus = async () => {
      try {
        const response = await fetch(
          `${API}/api/signzy/checkAAStatus?requestId=${encodeURIComponent(consentRequestId)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            credentials: "include",
            signal: controller.signal,
          },
        );
        const json = await response.json();
        if (cancelled) return;
        if (!response.ok || !json.success) {
          const message = json.message || "Unable to check consent status";
          if ([400, 401, 403, 404].includes(response.status)) {
            setError(message);
            return;
          }
          throw new Error(message);
        }
        const latestStatus = String(json.consentStatus || "PENDING")
          .trim()
          .toUpperCase();
        // Transactions or a statement PDF are saved: the step is complete, stop polling.
        if (json.hasStatement === true) {
          setConsentReceived(true);
          setRequiresRerun(false);
          setShowAggregator(false);
          setError("");
          onChange("aaStatus", "linked");
          onChange("aaDone", true);
          if (advanceOnApprovalRef.current) {
            advanceOnApprovalRef.current = false;
            onComplete();
          }
          return;
        }
        if (latestStatus === "ACTIVE" && json.canContinue !== true) {
          setRequiresRerun(true);
          setShowAggregator(false);
          onChange("aaDone", false);
          onChange("aaStatus", "refresh_required");
          setError(
            "Please run Account Aggregator again. Consent must be active, updated less than 15 days ago, and updated in the current month.",
          );
          return;
        }
        if (
          ["REJECTED", "REVOKED", "EXPIRED", "FAILED", "ERROR"].includes(
            latestStatus,
          )
        ) {
          setError(
            `Consent is ${latestStatus.toLowerCase()}. Please restart Account Aggregator.`,
          );
          onChange("aaDone", false);
          onChange("aaStatus", "failed");
          setShowAggregator(false);
          const failedSession = {
            ...sessionInfo,
            consent_status: latestStatus,
          };
          if (sessionInfo.consent_status !== latestStatus) {
            setSessionInfo(failedSession);
            onChange("aaSession", failedSession);
          }
          return;
        }
        if (latestStatus === "ACTIVE") {
          setConsentReceived(true);
          setRequiresRerun(false);
          setShowAggregator(false);
          setError("");
          if (
            sessionInfo.consent_status !== "ACTIVE" ||
            sessionInfo.updatedAt !== json.updatedAt
          ) {
            const activeSession = {
              ...sessionInfo,
              consent_status: "ACTIVE",
              updatedAt: json.updatedAt,
            };
            setSessionInfo(activeSession);
            onChange("aaSession", activeSession);
          }
          onChange("aaStatus", "linked");
          onChange("aaDone", true);
          if (advanceOnApprovalRef.current) {
            advanceOnApprovalRef.current = false;
            onComplete();
          }
          return;
        }
        setError("");
      } catch (err) {
        if (cancelled) return;
        setError(err.message || "Unable to check consent status");
      }
      if (!cancelled) timer = window.setTimeout(checkStatus, 3000);
    };
    checkStatus();
    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [
    consentRequestId,
    sessionInfo,
    token,
    onChange,
    onComplete,
    statusCheckVersion,
  ]);

  return (
    <div className="alp-step-body">
      <div className="alp-aa-header-intro">
        <h3>Provide Bank Statements</h3>
        <p>
          Securely link your bank account to fetch your statements
          automatically.
        </p>
      </div>

      <div className="alp-aa-choices-grid">
        <div
          className={`alp-aa-choice-card recommended ${selectedAaMethod !== "manual_upload" ? "active" : ""}`}
          onClick={() => {
            onChange("aaMethod", "account_aggregator");
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) =>
            (e.key === "Enter" || e.key === " ") &&
            onChange("aaMethod", "account_aggregator")
          }
          style={{
            opacity: selectedAaMethod === "manual_upload" ? 0.5 : 1,
            cursor: "pointer",
          }}
        >
          <h4>Account Aggregator</h4>
          <p>Securely link your bank account. No paperwork needed.</p>
          <div className="alp-aa-metric-group">
            <div className="alp-aa-metric">
              <span>Mobile No.</span>
              {editingMobile ? (
                <div
                  className="alp-aa-mobile-edit"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    autoFocus
                    placeholder="10-digit mobile"
                    value={mobileDraft}
                    onChange={(e) => {
                      setMobileDraft(
                        e.target.value.replace(/\D/g, "").slice(0, 10),
                      );
                      setMobileError("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveMobile();
                      if (e.key === "Escape") {
                        setEditingMobile(false);
                        setMobileError("");
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="alp-aa-mobile-save"
                    onClick={saveMobile}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="alp-aa-mobile-cancel"
                    onClick={() => {
                      setEditingMobile(false);
                      setMobileError("");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="alp-aa-mobile-view">
                  <strong
                    className={mobileValid ? "" : "alp-aa-mobile-missing"}
                  >
                    {mobile || "Not found"}
                  </strong>
                  {/* <button
                    type="button"
                    className="alp-aa-mobile-edit-btn"
                    disabled={consentReceived || loading}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMobileDraft(mobile);
                      setMobileError("");
                      setEditingMobile(true);
                    }}
                  >
                    Edit
                  </button> */}
                </div>
              )}
            </div>
          </div>

          {mobileError && (
            <div className="alp-aa-mobile-error">{mobileError}</div>
          )}
          <div className="alp-aa-mobile-note">
            The consent OTP is sent to this number.
            {toIndianMobile(data.aaMobile) &&
            toIndianMobile(data.aaMobile) !== profileMobile
              ? " Using an edited number instead of your registered one."
              : ""}
          </div>
          {consentReceived && (
            <div
              className="alp-alert alp-alert-success"
              style={{ marginTop: "0.75rem" }}
            >
              Consent is active. You can continue to the next step.
            </div>
          )}
          {sessionInfo?.redirectUrl && !consentReceived && !consentFailed && (
            <div
              className="alp-alert alp-alert-warning"
              style={{
                marginTop: "0.75rem",
                // display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                justifyContent: "space-between",
                background: "#fffbeb",
                border: "1px solid #fef3c7",
                borderRadius: "8px",
                padding: "0.75rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  minWidth: 0,
                }}
              >
                <span style={{ fontSize: "1.1rem" }}>⏳</span>
                <div style={{ minWidth: 0 }}>
                  <strong style={{ color: "#b45309" }}>
                    Pending Verification
                  </strong>
                  <div
                    style={{
                      fontSize: "0.82rem",
                      color: "#b45309",
                      opacity: 0.85,
                      marginTop: "2px",
                    }}
                  >
                    Complete consent on this page. We check your consent status
                    automatically.
                  </div>
                </div>
              </div>
              <button
                className="alp-btn-ghost"
                disabled={loading || editingMobile || !mobileValid}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange("aaSession", null);
                  onChange("aaDone", false);
                  setSessionInfo(null);
                  startAggregator();
                }}
                style={{
                  padding: "0.4rem 0.8rem",
                  fontSize: "0.8rem",
                  border: "1px solid #fcd34d",
                  borderRadius: "4px",
                  background: "white",
                  color: "#b45309",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                Restart
              </button>
            </div>
          )}
          {error && selectedAaMethod !== "manual_upload" && (
            <AlertToast title="Error" message={error} />
          )}
          <div className="alp-aa-choice-actions">
            <button
              className="alp-btn-primary"
              onClick={(e) => {
                e.stopPropagation();
                const isErrorStatus = consentFailed;
                if (
                  sessionInfo?.redirectUrl &&
                  !consentReceived &&
                  !isErrorStatus
                ) {
                  setError("");
                  setShowAggregator(true);
                  setStatusCheckVersion((version) => version + 1);
                } else {
                  if (isErrorStatus) {
                    onChange("aaSession", null);
                    setSessionInfo(null);
                  }
                  startAggregator();
                }
              }}
              disabled={
                loading || consentReceived || editingMobile || !mobileValid
              }
              style={{
                width: "100%",
                opacity: consentReceived || !mobileValid ? 0.6 : 1,
                cursor:
                  consentReceived || !mobileValid ? "not-allowed" : "pointer",
              }}
            >
              {consentReceived
                ? "✓ Consent Active"
                : loading
                  ? "Starting AA…"
                  : consentFailed
                    ? "Restart Account Aggregator"
                    : sessionInfo?.redirectUrl
                      ? "Resume Account Aggregator"
                      : "Start Account Aggregator"}
            </button>
            {consentStatus === "ACTIVE" && !consentFailed && (
              <button
                type="button"
                className="alp-btn-ghost"
                style={{ width: "100%", marginTop: "0.75rem" }}
                disabled={loading || editingMobile || !mobileValid}
                onClick={(event) => {
                  event.stopPropagation();
                  onChange("salaryAccountDone", false);
                  startAggregator();
                }}
              >
                Run Account Aggregator again
              </button>
            )}
          </div>
        </div>
      </div>

      {showAggregator &&
        sessionInfo?.redirectUrl &&
        !consentReceived &&
        !consentFailed && (
          <AccountAggregatorPopup
            key={sessionInfo.sessionId}
            sessionInfo={sessionInfo}
            onClose={() => setShowAggregator(false)}
          />
        )}

      <style>{`
                @keyframes pulse { 0%, 100% { transform: scaleY(0.5); opacity: 0.5; } 50% { transform: scaleY(1); opacity: 1; } }
            `}</style>
    </div>
  );
}

export function StepAddressProof({ data, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFinalizing, setUploadFinalizing] = useState(false);
  // docType holds the backend documentType of the chosen proof.
  const [docType, setDocType] = useState("");
  const [residenceType, setResidenceType] = useState("");
  // null while loading; afterwards the residence types the backend allows.
  const [proofOptions, setProofOptions] = useState(null);
  const [proofOptionsError, setProofOptionsError] = useState("");
  const [uploadMsg, setUploadMsg] = useState("");
  const session = JSON.parse(localStorage.getItem("userSession") || "{}");
  const userId = session?.userId;
  const token = session?.token || "";

  // true = proof needed, false = Aadhaar address verified by distance,
  // undefined = the server has not told us yet (never treated as verified).
  const mismatch = data.addressMismatch;
  const done = data.addressProofDone;
  const checking = useReusableDocument({
    userId,
    token,
    prefix: "addressProof",
    enabled: mismatch === true,
    onChange,
    setMessage: setUploadMsg,
  });

  useEffect(() => {
    if (mismatch === false && !data.addressProofDone) {
      onChange("addressProofDone", true);
    }
  }, [mismatch, data.addressProofDone, onChange]);

  // Residence and document choices come from the company's conditional
  // rules on the server; nothing is hard-coded here.
  useEffect(() => {
    if (mismatch !== true) return undefined;
    let cancelled = false;
    setProofOptions(null);
    setProofOptionsError("");
    (async () => {
      try {
        const response = await fetch(
          `${API}/api/config/address-proof-options`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const result = await response.json();
        if (!response.ok || !result.success)
          throw new Error("Could not load address proof options");
        if (cancelled) return;
        const types = Array.isArray(result.residenceTypes)
          ? result.residenceTypes.filter(
              (type) => Array.isArray(type.documents) && type.documents.length,
            )
          : [];
        setProofOptions(types);
        setResidenceType((current) =>
          types.some((type) => type.key === current)
            ? current
            : types[0]?.key || "",
        );
        setDocType("");
      } catch {
        if (!cancelled)
          setProofOptionsError(
            "Could not load the accepted document types. Please refresh and try again.",
          );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mismatch, token]);

  const residenceOptions = proofOptions || [];
  const selectedResidence =
    residenceOptions.find((type) => type.key === residenceType) || null;
  const documentOptions = selectedResidence?.documents || [];

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || checking) return;

    if (!docType) {
      alert("Please select a document type first.");
      e.target.value = "";
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadFinalizing(false);
    setUploadMsg(`Uploading ${file.name}...`);

    const fd = new FormData();
    fd.append("document", file);
    fd.append("userId", userId);
    // documentType is the backend's own key, so the file lands in the matching
    // user_documents column.
    fd.append("documentType", docType);
    if (selectedResidence) fd.append("residenceType", selectedResidence.key);

    try {
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/api/documents/upload`);
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);

        xhr.upload.onprogress = (evt) => {
          if (!evt.lengthComputable) return;
          const pct = Math.min(100, Math.round((evt.loaded / evt.total) * 100));
          setUploadProgress(pct);
          if (pct >= 100) {
            setUploadFinalizing(true);
            setUploadMsg(`Upload complete. Finalizing...`);
          } else {
            setUploadMsg(`Uploading ${file.name}... ${pct}%`);
          }
        };

        xhr.onload = () => {
          try {
            const parsed = JSON.parse(xhr.responseText || "{}");
            if (xhr.status >= 200 && xhr.status < 300 && parsed.success) {
              resolve(parsed);
            } else {
              reject(new Error(parsed.message || "Upload failed"));
            }
          } catch {
            reject(new Error("Upload failed"));
          }
        };
        xhr.onerror = () => reject(new Error("Network error during upload"));
        xhr.send(fd);
      });

      onChange("addressProofFileName", file.name);
      onChange("addressProofDocumentId", result.document?.id || null);
      onChange("addressProofUploadedAt", new Date().toISOString());
      onChange("addressProofDocumentType", docType);
      onChange("addressProofResidenceType", selectedResidence?.key || "");
      onChange("addressProofDone", true);
      setUploadMsg(`Successfully uploaded ${file.name}`);
    } catch (error) {
      setUploadMsg(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
      setUploadFinalizing(false);
    }
  };

  if (mismatch === undefined) {
    return (
      <div className="alp-step-body" role="status">
        <div className="alp-bank-panel" style={{ textAlign: "center" }}>
          <p style={{ margin: 0 }}>
            Checking your Aadhaar address against your current location…
          </p>
        </div>
      </div>
    );
  }

  if (mismatch === false) {
    return (
      <div className="alp-step-body">
        <div
          className="alp-bank-panel"
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            background: "#f0fdf4",
            borderColor: "#bbf7d0",
            minHeight: "300px",
          }}
        >
          <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>✅</div>
          <h3 style={{ color: "#166534", margin: "0 0 0.5rem 0" }}>
            Aadhaar Address
          </h3>
          <p
            style={{
              color: "#15803d",
              margin: 0,
              fontSize: "1rem",
              maxWidth: "400px",
            }}
          >
            You can continue with your Aadhaar address without enabling
            location.
          </p>
          <div
            style={{
              marginTop: "1.5rem",
              background: "rgba(255,255,255,0.7)",
              padding: "1rem",
              borderRadius: "12px",
              border: "1px solid rgba(22, 101, 52, 0.2)",
              width: "100%",
              maxWidth: "500px",
              textAlign: "left",
              fontSize: "0.9rem",
              color: "#166534",
            }}
          >
            <div
              style={{
                marginBottom: "0.5rem",
                display: "flex",
                gap: "0.5rem",
                alignItems: "flex-start",
              }}
            >
              <span style={{ fontSize: "1.1rem" }}>🏠</span>
              <div>
                <strong>Aadhaar Address:</strong>
                <div style={{ marginTop: "0.2rem", lineHeight: "1.4" }}>
                  {data.aadhaarAddress || "N/A"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="alp-step-body">
      <div className="alp-bank-grid">
        <div className="alp-bank-panel">
          <div
            className="alp-alert alp-aa-error"
            style={{ marginTop: "0", padding: "1rem" }}
          >
            <strong>Mismatch Detected</strong> <br />
            <span
              style={{
                fontSize: "0.875rem",
                marginTop: "0.25rem",
                display: "block",
              }}
            >
              Additional address proof is required for your saved application.
              Please provide an alternative address proof.
            </span>
            <div
              style={{
                marginTop: "1rem",
                background: "rgba(255,255,255,0.7)",
                padding: "0.75rem",
                borderRadius: "8px",
                border: "1px solid rgba(220, 38, 38, 0.2)",
                textAlign: "left",
                fontSize: "0.85rem",
                color: "#991b1b",
              }}
            >
              <div style={{ marginBottom: "0.5rem" }}>
                <strong>Aadhaar Address:</strong> {data.aadhaarAddress || "N/A"}
              </div>
            </div>
          </div>

          <div className="alp-bank-fields" style={{ marginTop: "1.5rem" }}>
            <div className="alp-field" style={{ gridColumn: "1 / -1" }}>
              <label htmlFor="alp-residence-type">Select Residence Type</label>
              {proofOptionsError ? (
                <div
                  className="alp-alert alp-aa-error"
                  role="alert"
                  style={{ marginTop: 0 }}
                >
                  {proofOptionsError}
                </div>
              ) : proofOptions === null ? (
                <div className="alp-input" role="status" aria-busy="true">
                  Loading accepted document types…
                </div>
              ) : residenceOptions.length === 0 ? (
                <div
                  className="alp-alert alp-aa-error"
                  role="alert"
                  style={{ marginTop: 0 }}
                >
                  No address proof types are configured yet. Please contact
                  support.
                </div>
              ) : (
                <select
                  id="alp-residence-type"
                  className="alp-input"
                  value={residenceType}
                  onChange={(e) => {
                    setResidenceType(e.target.value);
                    setDocType("");
                  }}
                  disabled={uploading || checking}
                >
                  {residenceOptions.map((type) => (
                    <option key={type.key} value={type.key}>
                      {type.label}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {selectedResidence && (
              <div className="alp-field" style={{ gridColumn: "1 / -1" }}>
                <label htmlFor="alp-address-doc-type">
                  Select Document Type
                </label>
                <select
                  id="alp-address-doc-type"
                  className="alp-input"
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                  disabled={uploading || checking}
                >
                  <option value="">-- Select --</option>
                  {documentOptions.map((document) => (
                    <option
                      key={document.documentType}
                      value={document.documentType}
                    >
                      {document.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {docType && (
            <div
              className="alp-bank-actions"
              style={{ position: "relative", marginTop: "1.5rem" }}
            >
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                style={{
                  position: "absolute",
                  inset: 0,
                  opacity: 0,
                  cursor: uploading ? "not-allowed" : "pointer",
                  width: "100%",
                  zIndex: 10,
                }}
                disabled={uploading || checking}
              />
              <button
                className="alp-btn-ghost"
                disabled={uploading || checking}
                style={{
                  width: "100%",
                  border: "2px dashed #cbd5e1",
                  background: "#f8fafc",
                  padding: "1.5rem",
                  height: "auto",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <span style={{ fontSize: "2rem" }}>📄</span>
                <span style={{ fontWeight: 600, color: "#334155" }}>
                  {uploading
                    ? uploadFinalizing
                      ? "Finalizing…"
                      : "Uploading…"
                    : done
                      ? "Re-upload Document"
                      : "Click to Upload File (PDF/Image)"}
                </span>
              </button>
            </div>
          )}

          {uploading && (
            <div
              className="alp-upload-progress"
              style={{ marginTop: "1.5rem" }}
            >
              <div
                className="alp-upload-progress-bar"
                style={{ width: `${uploadProgress}%` }}
              />
              <span>{uploadProgress}%</span>
            </div>
          )}

          {uploadMsg && !uploading && (
            <div
              style={{
                fontSize: "0.875rem",
                color: done ? "#10b981" : "#ef4444",
                textAlign: "center",
                marginTop: "1rem",
                fontWeight: 500,
              }}
            >
              {uploadMsg}
            </div>
          )}
        </div>

        {/* <div
          className="alp-bank-panel alp-bank-panel-saved"
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            background: done ? "#f8fafc" : "#f1f5f9",
            borderColor: done ? "#e2e8f0" : "#e2e8f0",
            minHeight: "300px",
          }}
        >
          {done && fileName ? (
            <>
              <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>📄</div>
              <h3 style={{ color: "#0f172a", margin: "0 0 0.5rem 0" }}>
                Document Uploaded
              </h3>
              <p
                style={{
                  color: "#475569",
                  wordBreak: "break-all",
                  margin: 0,
                  padding: "0 1rem",
                }}
              >
                {fileName}
              </p>
              <div
                style={{
                  marginTop: "1.5rem",
                  background: "#d1fae5",
                  color: "#065f46",
                  padding: "0.5rem 1rem",
                  borderRadius: "9999px",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                }}
              >
                ✓ Successfully Verified
              </div>
            </>
          ) : (
            <>
              <div
                style={{ fontSize: "4rem", marginBottom: "1rem", opacity: 0.3 }}
              >
                📍
              </div>
              <h3 style={{ color: "#475569", margin: "0 0 0.5rem 0" }}>
                Manual Verification
              </h3>
              <p style={{ color: "#64748b", margin: 0, padding: "0 1rem" }}>
                Please select a document type and upload a clear copy for manual
                address verification.
              </p>
            </>
          )}
        </div> */}
      </div>
    </div>
  );
}

// Fetches (and on first visit generates) the sanction letter for the
// approved lead, shows it inline and hands over to the E-Sign step.
export function StepSanctionLetter({ token, onProceed }) {
  const [state, setState] = useState({
    loading: true,
    error: "",
    letter: null,
  });

  // regenerate=true asks the backend for a fresh PDF instead of the stored one.
  const load = useCallback(
    async (regenerate = false) => {
      setState({ loading: true, error: "", letter: null });
      try {
        const response = await fetch(`${API}/api/generate/sanction/letter`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(regenerate ? { regenerate: true } : {}),
        });
        const json = await response.json().catch(() => ({}));
        if (!response.ok || !json.success) {
          throw new Error(
            json.message || "Could not load your sanction letter.",
          );
        }
        setState({ loading: false, error: "", letter: json });
      } catch (error) {
        setState({ loading: false, error: error.message, letter: null });
      }
    },
    [token],
  );

  useEffect(() => {
    load();
  }, [load]);

  const { loading, error, letter } = state;
  const url = letter?.sanctionLetterUrl || "";
  const signed =
    String(letter?.sanctionLetterStatus || "").toUpperCase() === "SIGNED";
  const canProceed = !loading && !error && !!url;
  // The fragment hides the PDF viewer's thumbnail pane and fits the page to
  // the frame width; it is not sent to S3, so the signed URL stays valid.
  const previewUrl = url ? `${url}#navpanes=0&view=FitH` : "";
  return (
    <div className="alp-step-body">
      <section className="alp-sanction" aria-busy={loading}>
        <div className="alp-sanction-toolbar">
          <span
            className={`alp-sanction-status${signed ? " is-signed" : ""}`}
            aria-live="polite"
          >
            {loading
              ? "Preparing your sanction letter…"
              : signed
                ? "✓ Sanction letter e-signed"
                : "Review the letter, then continue to e-sign it with Aadhaar OTP."}
          </span>
          <button
            type="button"
            className="alp-btn-primary alp-sanction-esign"
            onClick={onProceed}
            disabled={!canProceed}
            aria-disabled={!canProceed}
          >
            {signed ? "View e-sign status" : "Proceed to E-Sign"}
          </button>
        </div>
        <div className="alp-sanction-preview">
          {loading ? (
            <div className="alp-sanction-skeleton" aria-hidden="true">
              <span className="w-40" />
              <span className="w-70" />
              <span className="w-55" />
              <span className="w-90" />
              <span className="w-85" />
              <span className="w-60" />
            </div>
          ) : error ? (
            <div className="alp-sanction-error" role="alert">
              <span className="alp-sanction-error-icon" aria-hidden="true">
                !
              </span>
              <div>
                <strong>We couldn't load your sanction letter</strong>
                <p>{error}</p>
              </div>
              <button
                type="button"
                className="alp-btn-primary"
                onClick={() => load()}
              >
                Try again
              </button>
            </div>
          ) : (
            <>
              <iframe
                className="alp-sanction-frame"
                title="Sanction letter"
                src={previewUrl}
              />
              {/* Phones: most mobile browsers won't render a PDF inside a frame. */}
              <div className="alp-sanction-mobile">
                <span className="alp-sanction-mobile-icon" aria-hidden="true">
                  📄
                </span>
                <strong>Sanction Letter – {letter?.applicationNo ?? ""}</strong>
                <p>Open the PDF to review and save it on your phone.</p>
                <a
                  className="alp-btn-primary"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open sanction letter
                </a>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

// Step 9: Aadhaar OTP e-sign of the sanction letter through Signzy
// Contract360. The backend creates a Signzy contract from the stored letter
// PDF and returns a signing URL; the borrower enters Aadhaar + OTP on
// Signzy's page inside a full-screen overlay (or a new tab when the browser
// refuses to embed it). Signzy redirects that page to /esign/<token>, which
// posts { type: "esign-return", status } back to this window. The backend
// then pulls the signed PDF from Signzy and stores it on the lead, replacing
// the unsigned letter. No Aadhaar data is entered or kept in this app.
const ESIGN_FINALIZE_MAX_POLLS = 40;

export function StepEsign({ token, onBack }) {
  const [status, setStatus] = useState({
    loading: true,
    error: "",
    data: null,
  });
  const [consent, setConsent] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState("");
  const [session, setSession] = useState(null); // { esignUrl, token, contractId }
  const [overlayOpen, setOverlayOpen] = useState(false);
  // idle | finalizing | failed | timeout
  const [phase, setPhase] = useState("idle");
  const [checking, setChecking] = useState(false);
  const finalizePolls = useRef(0);

  const authHeaders = useMemo(
    () => ({
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    [token],
  );

  // ?sync=1 asks the backend to re-pull the contract from Signzy (throttled),
  // so completion shows within seconds even if a webhook is late.
  const loadStatus = useCallback(
    async (sync = false) => {
      try {
        const response = await fetch(
          `${API}/api/esign/sanction-letter/status${sync ? "?sync=1" : ""}`,
          { headers: authHeaders },
        );
        const json = await response.json().catch(() => ({}));
        if (!response.ok || !json.success) {
          throw new Error(json.message || "Could not load e-sign status.");
        }
        setStatus({ loading: false, error: "", data: json });
        return json;
      } catch (error) {
        setStatus((prev) => ({
          loading: false,
          error: error.message,
          data: prev.data,
        }));
        return null;
      }
    },
    [authHeaders],
  );

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const signed = status.data?.signed === true;
  const configured = status.data?.configured !== false;
  // Only the session created by this click is ever opened; the backend
  // creates a new Signzy contract on every start.
  const esignUrl = session?.esignUrl || "";
  const returnToken = session?.token || status.data?.token || "";
  const contractExpired = !signed && status.data?.failed === true;

  // Signzy redirected the overlay page to /esign/<token>?status=…; that page
  // reports back here.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const msg = event.data;
      if (!msg || msg.type !== "esign-return") return;
      if (returnToken && msg.token && msg.token !== returnToken) return;
      setOverlayOpen(false);
      if (msg.status === "success") {
        finalizePolls.current = 0;
        setPhase("finalizing");
        loadStatus(true);
      } else {
        setPhase("failed");
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [returnToken, loadStatus]);

  // Poll every 4 s while the overlay is open (a lost postMessage is harmless)
  // and every 3 s while finalizing (max ~2 minutes).
  useEffect(() => {
    if (signed || !(overlayOpen || phase === "finalizing")) return undefined;
    const timer = window.setInterval(
      async () => {
        const json = await loadStatus(true);
        if (json?.signed) return;
        if (phase === "finalizing") {
          finalizePolls.current += 1;
          if (finalizePolls.current >= ESIGN_FINALIZE_MAX_POLLS)
            setPhase("timeout");
        }
      },
      phase === "finalizing" ? 3000 : 4000,
    );
    return () => window.clearInterval(timer);
  }, [signed, overlayOpen, phase, loadStatus]);

  useEffect(() => {
    if (!signed) return;
    setOverlayOpen(false);
    setPhase("idle");
  }, [signed]);

  // Lock page scroll behind the overlay.
  useEffect(() => {
    if (!overlayOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [overlayOpen]);

  const startEsign = async ({ force = false } = {}) => {
    setStarting(true);
    setStartError("");
    try {
      const response = await fetch(`${API}/api/esign/sanction-letter/start`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(force ? { force: true } : {}),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || !json.success) {
        throw new Error(
          json.message || "Could not start the Aadhaar e-sign session.",
        );
      }
      if (!json.data?.esignUrl) {
        throw new Error(
          "The e-sign session is not ready yet. Please try again in a moment.",
        );
      }
      setSession({
        esignUrl: json.data.esignUrl,
        token: json.data.token || "",
        contractId: json.data.contractId || "",
      });
      setPhase("idle");
      setOverlayOpen(true);
      loadStatus();
    } catch (error) {
      setStartError(error.message);
    } finally {
      setStarting(false);
    }
  };

  // Every click starts a brand-new Signzy session; an earlier one is never reopened.
  const openSigning = () => startEsign({ force: true });

  const checkNow = async () => {
    setChecking(true);
    await loadStatus(true);
    setChecking(false);
  };

  const canSign = configured && !starting && consent;

  if (status.loading) {
    return (
      <div className="alp-step-body">
        <section className="alp-esign" aria-busy="true">
          <div className="alp-sanction-skeleton" aria-hidden="true">
            <span className="w-55" />
            <span className="w-85" />
            <span className="w-40" />
            <span className="w-70" />
          </div>
        </section>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="alp-step-body">
        <section className="alp-esign alp-esign-done" role="status">
          <div className="alp-esign-done-icon" aria-hidden="true">
            ✅
          </div>
          <h3>Sanction letter e-signed</h3>
          <p>
            Your Aadhaar e-signature has been recorded and the signed sanction
            letter is saved with your application. We will now proceed with
            disbursal of your loan.
          </p>
          <div className="alp-esign-actions">
            {status.data?.signedLetterUrl && (
              <a
                className="alp-btn-primary"
                href={status.data.signedLetterUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Download signed sanction letter
              </a>
            )}
            {status.data?.auditCertificateUrl && (
              <a
                className="alp-btn-ghost"
                href={status.data.auditCertificateUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Audit certificate
              </a>
            )}
            <button type="button" className="alp-btn-ghost" onClick={onBack}>
              View sanction letter
            </button>
          </div>
        </section>
      </div>
    );
  }

  const finalizing = phase === "finalizing";

  return (
    <div className="alp-step-body">
      <section className="alp-esign">
        {status.error && (
          <div className="alp-alert alp-aa-error" role="alert">
            {status.error}
          </div>
        )}

        {finalizing ? (
          <div className="alp-esign-pending" role="status" aria-live="polite">
            <div className="alp-esign-pending-head">
              <span className="alp-esign-badge">Finalizing</span>
              <h3>Finalizing your e-sign…</h3>
              <p>
                Your signature was received. We are fetching the signed sanction
                letter from the e-sign provider. This usually takes a few
                seconds.
              </p>
            </div>
            <div className="alp-esign-progress" aria-hidden="true">
              <span />
            </div>
          </div>
        ) : (
          <>
            <div className="alp-esign-intro">
              <h3>Accept your offer with Aadhaar e-sign</h3>
              <p>
                Signing the sanction letter is legally binding and confirms you
                accept the loan terms. You will enter your Aadhaar number on the
                secure e-sign page and verify with an OTP sent to the mobile
                number linked to your Aadhaar.
              </p>
            </div>

            {phase === "failed" && (
              <div className="alp-alert alp-aa-error" role="alert">
                The Aadhaar e-sign was not completed. You can try again.
              </div>
            )}
            {phase === "timeout" && (
              <div className="alp-alert alp-alert-warning" role="alert">
                We have not received confirmation from the e-sign provider yet.
                If you completed the OTP, use “I have signed – check status” in
                a minute; otherwise start the signing again.
              </div>
            )}
            {contractExpired && (
              <div className="alp-alert alp-alert-warning" role="alert">
                Your previous signing session has expired or was cancelled
                {status.data?.contractStatus
                  ? ` (${status.data.contractStatus.toLowerCase()})`
                  : ""}
                . Start again to get a new one.
              </div>
            )}

            <div className="alp-esign-form">
              {!configured && (
                <div className="alp-alert alp-aa-error" role="alert">
                  E-sign is not available right now. Please try again later.
                </div>
              )}
              <ol className="alp-esign-steps">
                <li>
                  Click “Sign with Aadhaar OTP” to open the secure e-sign page.
                </li>
                <li>Enter your Aadhaar / VID and request the OTP.</li>
                <li>
                  Submit the OTP to sign every page of the sanction letter.
                </li>
                <li>You will be brought back here automatically.</li>
              </ol>
              <label className="alp-esign-consent">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  disabled={starting}
                />
                <span>
                  I have read the sanction letter and agree to sign it
                  electronically using Aadhaar OTP. I consent to my name and
                  Aadhaar details being verified by the e-sign provider for this
                  signature.
                </span>
              </label>
              {startError && (
                <div className="alp-alert alp-aa-error" role="alert">
                  {startError}
                </div>
              )}
              <div className="alp-esign-actions">
                <button
                  type="button"
                  className="alp-btn-primary"
                  onClick={openSigning}
                  disabled={!canSign}
                >
                  {starting
                    ? "Preparing your e-sign session…"
                    : "Sign with Aadhaar OTP"}
                </button>
                {(esignUrl ||
                  status.data?.contractStatus ||
                  phase !== "idle") && (
                  <button
                    type="button"
                    className="alp-btn-ghost"
                    onClick={checkNow}
                    disabled={checking}
                  >
                    {checking ? "Checking…" : "I have signed – check status"}
                  </button>
                )}
              </div>
              {esignUrl && (
                <p className="alp-esign-hint">
                  Signing page not loading inside the app?{" "}
                  <a href={esignUrl} target="_blank" rel="noopener noreferrer">
                    Open it in a new tab
                  </a>
                  , then come back here.
                </p>
              )}
            </div>
          </>
        )}

        <div className="alp-esign-footer">
          <button type="button" className="alp-sanction-link" onClick={onBack}>
            ← Back to sanction letter
          </button>
        </div>
      </section>

      {overlayOpen && esignUrl && (
        <EsignOverlay
          esignUrl={esignUrl}
          onClose={() => setOverlayOpen(false)}
        />
      )}
    </div>
  );
}

// Full-screen overlay hosting Signzy's e-sign page. The ESP may refuse to be
// embedded in some browsers, so a "new tab" fallback is always offered.
function EsignOverlay({ esignUrl, onClose }) {
  return createPortal(
    <div
      className="alp-esign-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Aadhaar e-sign"
    >
      <div className="alp-esign-overlay-bar">
        <div className="alp-esign-overlay-title">
          <strong>Aadhaar e-sign</strong>
          <span>
            Complete the OTP on the secure page below. Do not close this window
            until you are brought back.
          </span>
        </div>
        <div className="alp-esign-overlay-actions">
          <a
            className="alp-btn-ghost"
            href={esignUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in a new tab
          </a>
          <button type="button" className="alp-btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      <iframe
        className="alp-esign-overlay-frame"
        title="Aadhaar e-sign"
        src={esignUrl}
        allow="camera *; microphone *; geolocation *"
      />
    </div>,
    document.body,
  );
}

function StepKycHandoff({ data, onChange }) {
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [kycLoading, setKycLoading] = useState(false);
  const [kycError, setKycError] = useState("");
  const [kycInProgress, setKycInProgress] = useState(false);
  const session = JSON.parse(localStorage.getItem("userSession") || "{}");
  const userId = session?.userId;
  const token = session?.token || "";

  const loadProfile = useCallback(async () => {
    if (!userId) {
      setLoadingProfile(false);
      return;
    }

    try {
      const response = await fetch(`${API}/api/auth/profile/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const json = await response.json();
      if (response.ok && json.success) {
        setProfile(json);
        const kycStatusLocal = String(
          json.user?.kycStatus || json.user?.kyc_status || "pending",
        ).toLowerCase();
        const panName = json.user?.full_name || "";
        const aadhaarName = json.kycRecords?.aadhaar?.aadhaar_name || "";

        let finalStatus = kycStatusLocal;
        let reason = "";

        // Frontend validation override: STRICT mismatch check has been removed as requested.

        // If aadhaar record has verified data but kyc_status wasn't updated, treat as under_review
        const aadhaarStatus = String(
          json.kycRecords?.aadhaar?.status || "",
        ).toUpperCase();
        const aadhaarVerified =
          !!(
            json.kycRecords?.aadhaar?.name ||
            json.kycRecords?.aadhaar?.aadhaar_name ||
            json.kycRecords?.aadhaar?.uid
          ) || aadhaarStatus === "COMPLETED";
        if (finalStatus === "pending" && aadhaarVerified) {
          finalStatus = "under_review";
        }

        onChange("kycStatus", finalStatus);
        onChange(
          "kycDone",
          ["completed", "verified", "under_review"].includes(finalStatus),
        );
        onChange(
          "addressMismatch",
          json.kycRecords?.aadhaar?.addressMismatch !== false,
        );
        const phoneMismatch = json.kycRecords?.aadhaar?.phoneMismatch === true;
        onChange("phoneMismatch", phoneMismatch);
        const aadhaarMismatch =
          json.kycRecords?.aadhaar?.aadhaarMismatch === true;
        onChange("aadhaarMismatch", aadhaarMismatch);
        onChange("aadhaarAddress", json.kycRecords?.aadhaar?.address || "");
        onChange("applicantName", aadhaarName || panName || "");
        onChange("gpsLatitude", json.user?.lastLatitude || null);
        onChange("gpsLongitude", json.user?.lastLongitude || null);

        const aadhaarAddressValue = String(
          json.kycRecords?.aadhaar?.address ||
            json.kycRecords?.aadhaar?.aadhaar_address ||
            "",
        );
        const aadhaarPincode =
          aadhaarAddressValue.match(/\b[1-9]\d{5}\b/)?.[0] || "";
        onChange("aadhaarPincode", aadhaarPincode);

        if (finalStatus === "rejected") {
          if (!reason) {
            reason =
              "KYC Verification Failed. Please ensure your Aadhaar matches your PAN details and retry.";
            try {
              const kycDataObj =
                typeof json.user?.kyc_data === "string"
                  ? JSON.parse(json.user.kyc_data)
                  : json.user?.kyc_data || {};
              if (kycDataObj.mismatchReason) reason = kycDataObj.mismatchReason;
            } catch (e) {}
          }
          setKycError(reason);
        } else if (aadhaarMismatch) {
          setKycError(AADHAAR_MISMATCH_MESSAGE);
        } else if (phoneMismatch) {
          setKycError(PHONE_MISMATCH_MESSAGE);
        } else if (finalStatus !== "pending") {
          setKycError("");
        }
      } else {
        // The server could not confirm KYC, so a flag restored from a saved
        // draft must not let the applicant continue past this step.
        onChange("kycDone", false);
        setKycError(
          json?.message ||
            "We could not load your KYC status. Please refresh the page or try again.",
        );
      }
    } catch {
      onChange("kycDone", false);
      setKycError(
        "We could not reach the server to check your KYC status. Please refresh the page or try again.",
      );
    } finally {
      setLoadingProfile(false);
    }
  }, [onChange, token, userId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const user = profile?.user || {};
  const records = profile?.kycRecords || {};
  const aadhaarRecord = records.aadhaar || {};
  const aadhaarRecordStatus = String(aadhaarRecord.status || "").toUpperCase();
  const aadhaarHasData =
    !!(aadhaarRecord.name || aadhaarRecord.aadhaar_name || aadhaarRecord.uid) ||
    aadhaarRecordStatus === "COMPLETED";
  const kycDone =
    ["completed", "verified", "under_review"].includes(
      String(user.kycStatus || user.kyc_status || "").toLowerCase(),
    ) || aadhaarHasData;
  const kycMismatch = !!(data.phoneMismatch || data.aadhaarMismatch);
  // One card at a time: the Digilocker action until KYC is done, then the
  // fetched Aadhaar details. A mismatch needs a retry, so it shows both.
  const showKycAction = !kycDone || kycMismatch;

  // Poll while KYC is in progress
  useEffect(() => {
    if (!kycInProgress || (kycDone && !kycMismatch)) return;
    const iv = setInterval(() => {
      loadProfile();
    }, 5000);
    return () => clearInterval(iv);
  }, [kycInProgress, kycDone, kycMismatch, loadProfile]);
  // Auto-stop polling when kyc is done
  useEffect(() => {
    if (kycDone && !kycMismatch && kycInProgress) {
      setKycInProgress(false);
    }
  }, [kycDone, kycMismatch, kycInProgress]);

  const displayName =
    user.name || aadhaarRecord.name || session?.name || "Applicant";
  const aadhaarUid = String(
    aadhaarRecord.uid || aadhaarRecord.aadhaar_uid || "",
  ).trim();
  const aadhaarDob = String(
    aadhaarRecord.dob || aadhaarRecord.aadhaar_dob || user.dob || "",
  ).trim();
  const aadhaarGender = String(
    aadhaarRecord.gender || aadhaarRecord.aadhaar_gender || user.gender || "",
  ).trim();
  const aadhaarAddress = String(
    aadhaarRecord.address ||
      aadhaarRecord.aadhaar_address ||
      user.address ||
      "",
  ).trim();
  const aadhaarName = String(
    aadhaarRecord.name || aadhaarRecord.aadhaar_name || displayName || "",
  ).trim();
  const aadhaarRows = [
    { label: "Name", value: aadhaarName || "Pending" },
    { label: "UID", value: aadhaarUid || "Pending" },
    { label: "DOB", value: aadhaarDob || "Pending" },
    { label: "Gender", value: aadhaarGender || "Pending" },
    { label: "Address", value: aadhaarAddress || "Pending" },
  ];

  const startDigilocker = useCallback(async () => {
    setKycError("");
    setKycLoading(true);

    const width = 500;
    const height = 750;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popup = window.open(
      "about:blank",
      `DigilockerKYC_${Date.now()}`,
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes,status=no`,
    );

    try {
      const response = await fetch(`${API}/api/kyc/initiate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ userId }),
      });
      const json = await response.json();
      if (!response.ok || !json.success || !json.url) {
        throw new Error(json.message || "Failed to initiate Digilocker");
      }

      if (popup) {
        popup.location.href = json.url;
      } else {
        window.location.href = json.url;
      }
      setKycInProgress(true);
      // The backend wiped the previous KYC; refresh so old Aadhaar data and
      // any phone-mismatch state clear immediately.
      loadProfile();
    } catch (err) {
      setKycError(err.message || "Unable to open Digilocker");
      if (popup) popup.close();
    } finally {
      setKycLoading(false);
    }
  }, [loadProfile, token, userId]);

  return (
    <div className="alp-step-body">
      <div className="alp-aa-card">
        <div className="alp-aa-choices-grid alp-kyc-grid">
          {showKycAction && (
            <div className="alp-aa-choice-card recommended alp-kyc-overview-card">
              <div className="alp-aa-choice-badge">
                {kycDone ? "KYC Verified" : "Digilocker • Aadhaar"}
              </div>
              <div className="alp-aa-choice-icon">📜</div>
              <h4>Digilocker KYC</h4>
              <p>
                {kycDone
                  ? "Aadhaar verification completed successfully."
                  : "Complete Aadhaar verification on the dedicated KYC page."}
              </p>

              <div className="alp-kyc-quick-stats">
                <div className="alp-kyc-stat-chip">
                  <span>Status</span>
                  <strong>
                    {kycDone
                      ? "Verified"
                      : loadingProfile
                        ? "Loading"
                        : "Pending"}
                  </strong>
                </div>
                <div className="alp-kyc-stat-chip">
                  <span>Source</span>
                  <strong>Digilocker</strong>
                </div>
              </div>

              <div className="alp-aa-choice-actions">
                <button
                  className="alp-btn-primary"
                  onClick={startDigilocker}
                  disabled={kycLoading || (kycDone && !kycMismatch)}
                  style={{ width: "100%" }}
                >
                  {kycDone && !kycMismatch
                    ? "Digilocker KYC Completed"
                    : kycLoading
                      ? "Opening Digilocker..."
                      : kycError
                        ? "Retry Digilocker KYC"
                        : "Open Digilocker KYC"}
                </button>
              </div>
              {kycError && <AlertToast title="KYC" message={kycError} />}
            </div>
          )}

          {kycDone && (
            <div className="alp-aa-choice-card alt">
              <div className="alp-aa-choice-badge alt">Verified</div>
              <div className="alp-aa-choice-icon alt">🆔</div>
              <h4>Aadhaar Details</h4>
              <p>Verified Aadhaar data fetched from Digilocker.</p>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "1rem",
                  alignItems: "center",
                  marginBottom: "0.9rem",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "0.72rem",
                      fontWeight: 800,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      color: "#0755d9",
                    }}
                  >
                    Aadhaar Details
                  </div>
                  <div
                    style={{
                      fontSize: "1rem",
                      fontWeight: 800,
                      color: "#0f172a",
                      marginTop: "0.25rem",
                    }}
                  >
                    Verified Aadhaar
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gap: "0.4rem" }}>
                {aadhaarRows.map((row) => (
                  <div
                    key={row.label}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "0.5rem",
                      padding: "0.45rem 0.65rem",
                      borderRadius: "10px",
                      background: "#fff",
                      border: "1px solid #eef2f7",
                    }}
                  >
                    <span
                      style={{
                        color: "#64748b",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                      }}
                    >
                      {row.label}
                    </span>
                    <strong
                      style={{
                        color: "#0f172a",
                        fontSize: "0.8rem",
                        textAlign: "right",
                        wordBreak: "break-word",
                        maxWidth: "65%",
                      }}
                    >
                      {row.value}
                    </strong>
                  </div>
                ))}
              </div>
              {data.aadhaarPincode && (
                <div
                  className="alp-alert alp-alert-success"
                  style={{ marginTop: "0.85rem" }}
                >
                  <strong>Aadhaar Pincode</strong> {data.aadhaarPincode}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Per-step copy for the shared left card header and the decorative right-hand
// showcase. Display-only: nothing here reads or changes journey state.
const STAGE_SHOWCASE = {
  employment: {
    badge: "SMART ELIGIBILITY",
    title: "Instant Income Check",
    desc: "Your income details help us match you with the right loan amount.",
    orbit: ["₹", "💼", "📈", "🧾"],
    card: { title: "Eligibility", items: ["Income", "Employer", "Amount"] },
    features: [
      {
        icon: "⚡",
        tone: "blue",
        title: "Quick eligibility",
        text: "Get assessed in minutes, not days",
      },
      {
        icon: "🎯",
        tone: "green",
        title: "Tailored offer",
        text: "Loan amount based on your income",
      },
      {
        icon: "📄",
        tone: "violet",
        title: "No paperwork",
        text: "Fully digital application",
      },
      {
        icon: "🔒",
        tone: "amber",
        title: "Private & secure",
        text: "Your details are encrypted",
      },
    ],
  },
  aa: {
    badge: "YOUR DATA, YOUR CONTROL",
    title: "Secure Bank Connection",
    desc: "We use RBI-regulated Account Aggregators to securely fetch your bank statements.",
    orbit: ["🏦", "💳", "📊", "🏛️"],
    card: {
      title: "Bank Statements",
      items: ["Secure", "Instant", "Paperless"],
    },
    features: [
      {
        icon: "🛡️",
        tone: "green",
        title: "Bank-grade encryption",
        text: "Protected with industry-leading security",
      },
      {
        icon: "📄",
        tone: "blue",
        title: "No paperwork",
        text: "Instant and digital process",
      },
      {
        icon: "👁️",
        tone: "violet",
        title: "Read-only access",
        text: "We can only view your statements",
      },
      {
        icon: "🔐",
        tone: "amber",
        title: "RBI regulated",
        text: "Consent-based data sharing framework",
      },
    ],
  },
  digilocker: {
    badge: "GOVERNMENT VERIFIED",
    title: "Paperless Aadhaar KYC",
    desc: "Your identity is verified directly from DigiLocker with your consent.",
    orbit: ["🪪", "📜", "✅", "🔑"],
    card: { title: "KYC", items: ["Aadhaar", "Name", "Address"] },
    features: [
      {
        icon: "🏛️",
        tone: "blue",
        title: "Official source",
        text: "Verified via DigiLocker",
      },
      {
        icon: "⚡",
        tone: "green",
        title: "Instant verification",
        text: "Done in a couple of minutes",
      },
      {
        icon: "🤝",
        tone: "violet",
        title: "Consent-based",
        text: "Shared only with your approval",
      },
      {
        icon: "🔒",
        tone: "amber",
        title: "Secure",
        text: "Your identity data stays protected",
      },
    ],
  },
  address_proof: {
    badge: "PROOF OF RESIDENCE",
    title: "Quick Address Check",
    desc: "A clear document helps us verify your address without delays.",
    orbit: ["🏠", "📍", "📄", "✅"],
    card: {
      title: "Address Proof",
      items: ["Uploaded", "Encrypted", "Reviewed"],
    },
    features: [
      {
        icon: "⬆️",
        tone: "blue",
        title: "Upload in seconds",
        text: "Simple, guided upload",
      },
      {
        icon: "🔐",
        tone: "green",
        title: "Encrypted storage",
        text: "Documents are stored securely",
      },
      {
        icon: "🔍",
        tone: "violet",
        title: "Clear review",
        text: "A legible copy speeds things up",
      },
      {
        icon: "🎯",
        tone: "amber",
        title: "Single purpose",
        text: "Used only for verification",
      },
    ],
  },
  selfie: {
    badge: "FACE MATCH",
    title: "Selfie Verification",
    desc: "A quick selfie confirms it's really you applying.",
    orbit: ["📸", "🙂", "💡", "✅"],
    card: { title: "Selfie", items: ["Liveness", "Face match", "Done"] },
    features: [
      {
        icon: "⏱️",
        tone: "blue",
        title: "Takes seconds",
        text: "One quick capture",
      },
      {
        icon: "💡",
        tone: "green",
        title: "Good lighting",
        text: "Face the light for best results",
      },
      {
        icon: "🪪",
        tone: "violet",
        title: "Matched to Aadhaar",
        text: "Confirms your identity",
      },
      {
        icon: "🔒",
        tone: "amber",
        title: "Kept private",
        text: "Used only for verification",
      },
    ],
  },
  relation: {
    badge: "TRUSTED CONTACTS",
    title: "Your References",
    desc: "Two people who know you help us complete your application.",
    orbit: ["👥", "📞", "🤝", "✅"],
    card: { title: "References", items: ["Contact 1", "Contact 2", "Saved"] },
    features: [
      {
        icon: "👥",
        tone: "blue",
        title: "Two contacts",
        text: "Family, friends or colleagues",
      },
      {
        icon: "📞",
        tone: "green",
        title: "Contacted if needed",
        text: "Only when required",
      },
      {
        icon: "🔒",
        tone: "violet",
        title: "Kept private",
        text: "Details are stored securely",
      },
      {
        icon: "⚡",
        tone: "amber",
        title: "Quick to fill",
        text: "Takes under a minute",
      },
    ],
  },
  salary_account: {
    badge: "DISBURSAL READY",
    title: "Salary Account",
    desc: "Pick the account where your salary is credited each month.",
    orbit: ["🏦", "💸", "🗓️", "✅"],
    card: { title: "Account", items: ["Linked", "Verified", "Ready"] },
    features: [
      {
        icon: "🔗",
        tone: "blue",
        title: "Linked account",
        text: "Chosen from your connected banks",
      },
      {
        icon: "⚡",
        tone: "green",
        title: "Smooth disbursal",
        text: "Funds go to the right account",
      },
      {
        icon: "🗓️",
        tone: "violet",
        title: "Easy repayments",
        text: "Aligned with your salary date",
      },
      {
        icon: "🔒",
        tone: "amber",
        title: "Secure",
        text: "Bank details stay protected",
      },
    ],
  },
  sanction_letter: {
    badge: "LOAN APPROVED",
    title: "Sanction Letter",
    desc: "Your in-principle sanction is ready. Review the terms before disbursal.",
    orbit: ["📄", "✅", "₹", "🖊️"],
    card: { title: "Letter", items: ["Approved", "Generated", "Ready"] },
    features: [
      {
        icon: "✅",
        tone: "green",
        title: "Approved",
        text: "Our agent has approved your application",
      },
      {
        icon: "📄",
        tone: "blue",
        title: "Official letter",
        text: "Terms, amount and repayment date",
      },
      {
        icon: "⬇️",
        tone: "violet",
        title: "Download",
        text: "Keep a copy for your records",
      },
      {
        icon: "🔒",
        tone: "amber",
        title: "Secure",
        text: "Link is private and expires in an hour",
      },
    ],
  },
};

// Decorative right-hand panel: animated shield illustration plus per-step
// highlights. Display-only.
function ApplyStageShowcase({ stepKey, icon }) {
  const copy = STAGE_SHOWCASE[stepKey];
  return (
    <aside className="alp-aside" aria-label={copy.title}>
      <div className="alp-show" key={stepKey}>
        <div className="alp-show-bg" aria-hidden="true">
          <div className="alp-show-glow alp-show-glow-1" />
          <div className="alp-show-glow alp-show-glow-2" />
        </div>

        <div className="alp-show-text">
          <span className="alp-show-badge">{copy.badge}</span>
          <h3 className="alp-show-title">{copy.title}</h3>
          <p className="alp-show-desc">{copy.desc}</p>

          <ul className="alp-show-features">
            {copy.features.map((feature) => (
              <li key={feature.title}>
                <span className={`alp-show-feature-icon tone-${feature.tone}`}>
                  {feature.icon}
                </span>
                <div>
                  <strong>{feature.title}</strong>
                  <small>{feature.text}</small>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="alp-show-visual" aria-hidden="true">
          <div className="alp-show-orbit alp-show-orbit-1" />
          <div className="alp-show-orbit alp-show-orbit-2" />
          <span className="alp-show-dot alp-show-dot-1" />
          <span className="alp-show-dot alp-show-dot-2" />
          {copy.orbit.map((chip, index) => (
            <div
              key={chip + index}
              className={`alp-show-chip alp-show-chip-${index + 1}`}
            >
              {chip}
            </div>
          ))}
          <div className="alp-show-shield">
            <svg viewBox="0 0 100 116">
              <defs>
                <linearGradient id="alpShieldGrad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#93c5fd" />
                  <stop offset="55%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#1d4ed8" />
                </linearGradient>
              </defs>
              <path
                d="M50 4 L92 20 V56 C92 84 72 104 50 112 C28 104 8 84 8 56 V20 Z"
                fill="url(#alpShieldGrad)"
              />
              <path
                d="M50 14 L82 26 V56 C82 78 67 94 50 101 C33 94 18 78 18 56 V26 Z"
                fill="rgba(255,255,255,0.18)"
              />
            </svg>
            <span className="alp-show-shield-icon">{icon}</span>
          </div>
          <div className="alp-show-card">
            <strong>{copy.card.title}</strong>
            {copy.card.items.map((item) => (
              <span key={item}>
                <i>✓</i>
                {item}
              </span>
            ))}
          </div>
          <div className="alp-show-tagline">
            Your financial journey, simplified!
          </div>
        </div>
      </div>
    </aside>
  );
}

function StepRelations({ data, onChange, validationMessage = "" }) {
  return (
    <div className="alp-references-section">
      <div className="alp-references-heading">
        <span>👥</span> References
      </div>
      {REFERENCE_SLOTS.map(({ n, namePlaceholder, phonePlaceholder }) => (
        <div className="alp-ref-block" key={n}>
          <div className="alp-ref-caption">Reference {n}</div>
          <div className="alp-ref-fields">
            <div className="alp-field">
              <label>
                <span className="alp-field-icon">👤</span> Name *
              </label>
              <input
                type="text"
                placeholder={namePlaceholder}
                maxLength={50}
                value={data[`ref${n}_name`] || ""}
                onChange={(e) =>
                  onChange(`ref${n}_name`, e.target.value.replace(/\d/g, ""))
                }
              />
            </div>
            <div className="alp-field">
              <label>
                <span className="alp-field-icon">📞</span> Mobile *
              </label>
              <input
                type="tel"
                placeholder={phonePlaceholder}
                maxLength={10}
                value={data[`ref${n}_phone`] || ""}
                onChange={(e) =>
                  onChange(
                    `ref${n}_phone`,
                    e.target.value.replace(/\D/g, "").slice(0, 10),
                  )
                }
              />
            </div>
            <div className="alp-field">
              <label>
                <span className="alp-field-icon">🤝</span> Relation *
              </label>
              <select
                value={data[`ref${n}_relation`] || ""}
                onChange={(e) => {
                  onChange(`ref${n}_relation`, e.target.value);
                  if (e.target.value !== "Other")
                    onChange(`ref${n}_relation_other`, "");
                }}
              >
                <option value="">Select relation</option>
                {REFERENCE_RELATIONS.map((r) => (
                  <option
                    key={r}
                    value={r}
                    disabled={
                      UNIQUE_REFERENCE_RELATIONS.includes(r) &&
                      data[`ref${n === 1 ? 2 : 1}_relation`] === r
                    }
                  >
                    {r}
                  </option>
                ))}
              </select>
            </div>

            {data[`ref${n}_relation`] === "Other" && (
              <div className="alp-field alp-ref-other">
                <label>
                  <span className="alp-field-icon">✍️</span> Specify Relation *
                </label>
                <input
                  type="text"
                  placeholder="e.g. Neighbour"
                  maxLength={50}
                  value={data[`ref${n}_relation_other`] || ""}
                  onChange={(e) =>
                    onChange(`ref${n}_relation_other`, e.target.value)
                  }
                />
              </div>
            )}
          </div>
        </div>
      ))}
      {validationMessage && (
        <AlertToast title="References" message={validationMessage} />
      )}
    </div>
  );
}

function getRelationsValidationMessage(data) {
  for (const { n } of REFERENCE_SLOTS) {
    const name = String(data[`ref${n}_name`] || "").trim();
    if (!name) return `Please enter Reference ${n} name.`;
    if (!REFERENCE_NAME_RE.test(name))
      return `Reference ${n} name can contain only letters, not numbers.`;
    if (
      !/^[6-9]\d{9}$/.test(
        String(data[`ref${n}_phone`] || "").replace(/\D/g, ""),
      )
    ) {
      return `Please enter a valid 10-digit mobile number for Reference ${n}.`;
    }
    const relation = data[`ref${n}_relation`];
    if (!REFERENCE_RELATIONS.includes(relation))
      return `Please select the relation for Reference ${n}.`;
    if (
      relation === "Other" &&
      !CUSTOM_RELATION_RE.test(
        String(data[`ref${n}_relation_other`] || "").trim(),
      )
    ) {
      return `Please specify the relation for Reference ${n}.`;
    }
  }

  if (toIndianMobile(data.ref1_phone) === toIndianMobile(data.ref2_phone)) {
    return "Reference 1 and Reference 2 cannot have the same mobile number.";
  }

  if (
    UNIQUE_REFERENCE_RELATIONS.includes(data.ref1_relation) &&
    data.ref1_relation === data.ref2_relation
  ) {
    return `${data.ref1_relation} can only be selected for one reference. Please choose a different relation.`;
  }

  return "";
}

function getEmploymentValidationMessage(data) {
  if (!String(data.amount ?? "").trim()) {
    return "Please enter the loan amount you need.";
  }
  const askedLoanAmount = Number(data.amount);
  if (
    !Number.isSafeInteger(askedLoanAmount) ||
    askedLoanAmount < LOAN_AMOUNT_MIN ||
    askedLoanAmount > LOAN_AMOUNT_MAX
  ) {
    return `Loan amount must be between ₹${LOAN_AMOUNT_MIN.toLocaleString("en-IN")} and ₹${LOAN_AMOUNT_MAX.toLocaleString("en-IN")}.`;
  }

  const activeType = EMP_TYPES.includes(data.empType) ? data.empType : "";
  if (!activeType) return "Please select one income profile to continue.";

  const fields = EMPLOYMENT_FIELDS[activeType] || [];
  const invalidField = fields.find((field) =>
    getEmploymentFieldValidationMessage(data, field),
  );
  if (invalidField)
    return getEmploymentFieldValidationMessage(data, invalidField);

  return "";
}

function getEmploymentFieldValidationMessage(data, field) {
  if (field.key === "amount") {
    if (!String(data.amount ?? "").trim()) {
      return "Please enter the loan amount you need.";
    }
    const askedLoanAmount = Number(data.amount);
    if (
      !Number.isSafeInteger(askedLoanAmount) ||
      askedLoanAmount < LOAN_AMOUNT_MIN ||
      askedLoanAmount > LOAN_AMOUNT_MAX
    ) {
      return `Loan amount must be between ₹${LOAN_AMOUNT_MIN.toLocaleString("en-IN")} and ₹${LOAN_AMOUNT_MAX.toLocaleString("en-IN")}.`;
    }
  }

  if (field.required !== false && !String(data[field.key] || "").trim()) {
    return `Please fill ${field.label}.`;
  }

  if (field.key === "income") {
    const incomeValue = Number(data.income || 0);
    if (!Number.isFinite(incomeValue) || incomeValue <= 0) {
      return "Please enter a valid monthly income.";
    }
  }

  if (field.key === "salary_date" && String(data.salary_date || "").trim()) {
    const salaryDate = Number(data.salary_date);
    if (!Number.isInteger(salaryDate) || salaryDate < 1 || salaryDate > 31) {
      return "Please enter a date between 1 and 31.";
    }
  }

  if (field.key === "personal_email") {
    const personalEmail = String(data.personal_email || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(personalEmail)) {
      return "Please enter a valid personal email address.";
    }
  }

  if (field.key === "email") {
    const emailValue = String(data.email || "").trim();
    if (!emailValue) return "";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
      return "Please enter a valid professional email address.";
    }
    if (
      data.emailTaken &&
      emailValue.toLowerCase() === String(data.emailTaken).toLowerCase()
    ) {
      return "This email is already registered with another account.";
    }
    const publicDomains = [
      "gmail.com",
      "yahoo.com",
      "yahoo.co.in",
      "hotmail.com",
      "outlook.com",
      "rediff.com",
      "rediffmail.com",
      "icloud.com",
      "aol.com",
      "live.com",
    ];
    if (publicDomains.includes(emailValue.split("@")[1]?.toLowerCase())) {
      return "Please provide a valid professional/company email address (public domains are not allowed).";
    }
  }

  return "";
}

function StepEmployment({
  data,
  onChange,
  validationMessage = "",
  blockMessage = "",
}) {
  const emailTaken =
    !!data.emailTaken &&
    String(data.email || "")
      .trim()
      .toLowerCase() === String(data.emailTaken).toLowerCase();
  // Default employment type = Salaried
  const activeType = EMP_TYPES.includes(data.empType)
    ? data.empType
    : "Salaried";

  const fields = EMPLOYMENT_FIELDS[activeType] || [];

  const hasFieldValidationError = fields.some((field) =>
    getEmploymentFieldValidationMessage(data, field),
  );

  // Save Salaried as default in existing state/data
  useEffect(() => {
    if (!EMP_TYPES.includes(data.empType)) {
      onChange("empType", "Salaried");
    }
  }, [data.empType, onChange]);

  const handleEmploymentChange = (type) => {
    if (type === activeType) return;

    onChange("empType", type);
  };

  return (
    <div className="alp-step-body">
      <div className="alp-new-employment">
        {/* =========================
            ANIMATED BACKGROUND
        ========================== */}
        <div className="alp-emp-bg" aria-hidden="true">
          <div className="alp-emp-grid-bg" />

          <div className="alp-emp-orb alp-emp-orb-1" />
          <div className="alp-emp-orb alp-emp-orb-2" />
          <div className="alp-emp-orb alp-emp-orb-3" />

          <div className="alp-emp-float-card alp-emp-float-1">₹</div>

          <div className="alp-emp-float-card alp-emp-float-2">%</div>

          <div className="alp-emp-float-card alp-emp-float-3">↗</div>
        </div>

        {/* =========================
            CONTENT
        ========================== */}
        <div className="alp-emp-content">
          {/* Header */}
          <div className="alp-emp-new-header">
            <div className="alp-emp-header-icon">
              <span>₹</span>
            </div>

            <div className="alp-emp-header-copy">
              <span className="alp-emp-eyebrow">INCOME DETAILS</span>

              <h2>Tell us about your income</h2>

              {/* <p>
                Select your employment type and enter your income information to
                continue your loan application.
              </p> */}
            </div>

            <div className="alp-emp-secure">
              <span className="alp-secure-dot" />
              Secure & encrypted
            </div>
          </div>

          {/* =========================
              EMPLOYMENT SELECTOR
          ========================== */}
          <div className="alp-emp-selector">
            {EMP_TYPES.map((type) => {
              const option = EMPLOYMENT_OPTIONS[type];
              const selected = activeType === type;

              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => handleEmploymentChange(type)}
                  className={`alp-emp-type ${selected ? "active" : ""}`}
                >
                  <div className="alp-emp-type-left">
                    <div
                      className={`alp-emp-type-icon alp-emp-type-${option.tone}`}
                    >
                      {option.icon}
                    </div>

                    <div className="alp-emp-type-text">
                      <strong>{option.label}</strong>
                      <span>{option.desc}</span>
                    </div>
                  </div>

                  <div className="alp-emp-radio">
                    <span />
                  </div>

                  {selected && (
                    <div className="alp-selected-badge">Selected</div>
                  )}
                </button>
              );
            })}
          </div>

          {blockMessage && (
            <AlertToast title="Not eligible" message={blockMessage} />
          )}

          {!blockMessage && emailTaken && (
            <AlertToast
              title="Email already registered"
              message="This official email is already registered with another account. Please use a different email to continue."
            />
          )}

          {/* Validation */}
          {!blockMessage && validationMessage && !hasFieldValidationError && (
            <AlertToast title="Employment Info" message={validationMessage} />
          )}

          {/* =========================
              FORM
          ========================== */}
          <div key={activeType} className="alp-emp-form-card">
            <div className="alp-emp-form-head">
              <div>
                <span className="alp-form-small-title">
                  {activeType === "Salaried"
                    ? "SALARIED DETAILS"
                    : "BUSINESS DETAILS"}
                </span>

                <h3>
                  {activeType === "Salaried"
                    ? "Employment Information"
                    : "Self-Employment Information"}
                </h3>

                <p>
                  {activeType === "Salaried"
                    ? "Enter the details related to your current employment."
                    : "Tell us about your business or professional income."}
                </p>
              </div>

              <div className="alp-form-status">
                <span />
                {activeType}
              </div>
            </div>

            <div className="alp-new-fields-grid">
              {fields.map((f, index) => {
                const fieldError = getEmploymentFieldValidationMessage(data, f);

                return (
                  <div
                    className="alp-new-field"
                    key={`${activeType}-${f.key}`}
                    style={{
                      "--field-delay": `${index * 55}ms`,
                    }}
                  >
                    <label>
                      <span className="alp-new-field-icon">{f.icon}</span>

                      <span>
                        {f.label}

                        {f.required !== false && <em>*</em>}
                      </span>
                    </label>

                    <div
                      className={`alp-new-input-wrap ${
                        fieldError ? "has-error" : ""
                      }`}
                    >
                      <input
                        type={f.type || "text"}
                        placeholder={f.placeholder}
                        min={
                          f.key === "amount"
                            ? LOAN_AMOUNT_MIN
                            : f.key === "salary_date"
                              ? 1
                              : undefined
                        }
                        max={
                          f.key === "amount"
                            ? LOAN_AMOUNT_MAX
                            : f.key === "salary_date"
                              ? 31
                              : undefined
                        }
                        value={data[f.key] || ""}
                        onChange={(e) => {
                          const value = e.target.value;

                          if (f.key !== "amount" || value === "") {
                            onChange(f.key, value);
                            return;
                          }

                          const numericValue = Number(value);

                          onChange(
                            f.key,
                            numericValue > LOAN_AMOUNT_MAX
                              ? String(LOAN_AMOUNT_MAX)
                              : value,
                          );
                        }}
                        onBlur={(e) => {
                          if (f.key !== "amount" || e.target.value === "")
                            return;

                          const numericValue = Number(e.target.value);

                          if (
                            Number.isFinite(numericValue) &&
                            numericValue < LOAN_AMOUNT_MIN
                          ) {
                            onChange(f.key, String(LOAN_AMOUNT_MIN));
                          }
                        }}
                      />
                    </div>

                    {fieldError && (
                      <div className="alp-field-error">
                        <span>!</span>
                        {fieldError}
                      </div>
                    )}

                    {f.key === "amount" && !fieldError && (
                      <div className="alp-field-helper">
                        Loan range: ₹{LOAN_AMOUNT_MIN.toLocaleString("en-IN")} –
                        ₹{LOAN_AMOUNT_MAX.toLocaleString("en-IN")}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Bottom Security */}
            <div className="alp-emp-security-note">
              <div className="alp-security-icon">🔒</div>

              <div>
                <strong>Your information is protected</strong>
                <span>
                  Your financial information is encrypted and securely
                  processed.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ApplyLoanPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  // Self-employed applicants stop after Employment Info and see a thank-you screen.
  const [selfEmployedThanks, setSelfEmployedThanks] = useState(false);
  const [leadStatus, setLeadStatus] = useState(null);
  const [applicationBlocked, setApplicationBlocked] = useState(false);
  const [blockedEligibility, setBlockedEligibility] = useState(null);
  const [sanctionUnlocked, setSanctionUnlocked] = useState(false);
  const [approvedOffer, setApprovedOffer] = useState(null);
  const [creditCheck, setCreditCheck] = useState(null);
  const [savingStep, setSavingStep] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittedApplicationRef = useRef(null);
  const agentSubmittedRef = useRef(false);
  const autosaveRef = useRef(Promise.resolve());
  const [submitError, setSubmitError] = useState("");
  const [stepError, setStepError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [data, setData] = useState(() => {
    const locationVerified =
      sessionStorage.getItem("serviceableLocationVerified") === "true";
    return {
      amount: DEFAULT_LOAN_AMOUNT,
      tenure: "14",
      locationDone: locationVerified,
      pincodeServiceable: locationVerified,
    };
  });

  const [journeyReady, setJourneyReady] = useState(false);
  const [journeyLoadError, setJourneyLoadError] = useState("");
  const session = JSON.parse(localStorage.getItem("userSession") || "{}");
  const userId = session?.userId;
  const token = session?.token || "";
  const journeyHydratedRef = useRef(false);
  const sessionEmail = session?.email || "";
  const alreadySubmittedError = /already have an active or pending loan/i.test(
    String(submitError || ""),
  );

  const resetJourneyForNewApplication = useCallback(() => {
    localStorage.removeItem(`loan_journey_checkpoint_${userId}`);
    localStorage.removeItem(`loan_journey_draft_${userId}`);
    journeyHydratedRef.current = true;
    // Step 0 starts at the minimum loan amount; the applicant can change it.
    setData({
      amount: DEFAULT_LOAN_AMOUNT,
      tenure: "14",
      ...(sessionEmail ? { email: sessionEmail } : {}),
    });
    setStep(0);
    setSubmitted(false);
    setSubmitError("");
  }, [sessionEmail, userId]);

  useEffect(() => {
    if (!sessionEmail) return;
    setData((prev) => (prev.email ? prev : { ...prev, email: sessionEmail }));
  }, [sessionEmail]);

  useEffect(() => {
    let active = true;

    const loadJourneyData = async () => {
      if (!userId) {
        if (active) setJourneyReady(true);
        return;
      }

      try {
        const response = await fetch(`${API}/api/loan-journey/data`, {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        const json = await response.json().catch(() => ({}));

        if (!active) return;
        if (!response.ok || !json.success)
          throw new Error(
            "Unable to load your saved application. Please retry.",
          );

        let localCheckpoint = null;
        try {
          localCheckpoint = JSON.parse(
            localStorage.getItem(`loan_journey_checkpoint_${userId}`) || "null",
          );
        } catch {
          /* Ignore an invalid browser checkpoint and use the server. */
        }

        setCreditCheck(json.creditCheck || null);
        const eligibility = json.applicationEligibility || {};
        const canApply = eligibility.canApply === true;
        const eligibilityLeadStatus = String(
          eligibility.leadStatus || "",
        ).toUpperCase();
        const eligibilityLoanStatus = String(
          eligibility.loanStatus || "",
        ).toUpperCase();
        const hasOpenLoan =
          !!eligibilityLoanStatus && eligibilityLoanStatus !== "CLOSED";
        // An APPROVED lead that is not yet disbursed unlocks the Sanction
        // Letter step instead of the "application in progress" screen.
        if (!canApply && eligibilityLeadStatus === "APPROVED" && !hasOpenLoan) {
          setApplicationBlocked(false);
          setBlockedEligibility(null);
          setLeadStatus(eligibility.latestLead || null);
          setSanctionUnlocked(true);
          setSubmitted(false);
          journeyHydratedRef.current = true;
          setJourneyReady(true);
          setApprovedOffer(eligibility.latestLead || {});
          return;
        }
        setSanctionUnlocked(false);
        setApplicationBlocked(!canApply);
        setBlockedEligibility(canApply ? null : eligibility);
        if (!canApply) {
          setJourneyReady(true);
          return;
        }
        setSubmitted(false);
        const savedJourneyStatus = String(
          json.record?.status || "",
        ).toUpperCase();
        const savedCurrentStage = Number(json.record?.currentStage || 1);
        // currentStage is 1-based (step + 1), so sitting on the final Review step
        // saves STEPS.length — that is not a submission.
        // Preserve legacy stage 10 (Review before declaration was removed).
        const hasSavedSubmitStage =
          savedCurrentStage > Math.max(STEPS.length, 10) ||
          savedJourneyStatus === "COMPLETED";

        // If user has an old non-blocking loan AND their current draft is already completed, wipe it
        if (json.latestLoan && hasSavedSubmitStage) {
          resetJourneyForNewApplication();
          fetch(`${API}/api/loan-progress/clear`, {
            method: "POST",
            credentials: "include",
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          }).catch(() => {});
          return;
        }

        // If user has NO latest loan BUT their current draft is completed, wipe it
        if (!json.latestLoan && hasSavedSubmitStage) {
          resetJourneyForNewApplication();
          localStorage.removeItem(`loan_journey_draft_${userId}`);
          fetch(`${API}/api/loan-progress/clear`, {
            method: "POST",
            credentials: "include",
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          }).catch(() => {});
          return;
        }

        // Bound by the pre-v7 length (8) so legacy stages survive until remapped.
        const checkpoint = selectJourneyCheckpoint(
          json.record,
          localCheckpoint,
          Math.max(STEPS.length, 8),
        );
        const savedData = { ...checkpoint.journeyData };

        // Never restore API-computed verification flags from draft — let loadProfile own these
        delete savedData.kycDone;
        delete savedData.kycStatus;
        delete savedData.phoneMismatch;
        delete savedData.aadhaarMismatch;
        delete savedData.emailTaken;
        delete savedData.aaDone;
        delete savedData.bankDone;
        delete savedData.selfieDone;
        delete savedData.addressProofDone;
        // The distance verdict is recomputed by the server on every load; a
        // draft saved before the check ran must not skip the address proof.
        delete savedData.addressMismatch;
        delete savedData.addressProofNotRequired;
        delete savedData.salaryAccountDone;
        // GPS permission must be granted again for the current application session.
        delete savedData.locationDone;
        delete savedData.pincodeServiceable;
        delete savedData.aadhaarPincodeServiceable;

        // Restore KYC flags from backend's live computation (top-level fields)
        if (json.kycDone !== undefined) savedData.kycDone = json.kycDone;
        if (json.kycStatus !== undefined) savedData.kycStatus = json.kycStatus;
        if (json.phoneMismatch !== undefined)
          savedData.phoneMismatch = json.phoneMismatch === true;
        if (json.aadhaarMismatch !== undefined)
          savedData.aadhaarMismatch = json.aadhaarMismatch === true;
        // Only an explicit false from the server lets the applicant continue
        // on the Aadhaar address alone.
        savedData.addressMismatch = json.addressMismatch !== false;

        const savedJourneyVersion = checkpoint.journeyData?.journeyVersion;
        let savedStage = Math.max(1, checkpoint.currentStage);
        // Map selfie-first drafts to the restored employment-first order.
        if (savedJourneyVersion === 5) {
          savedStage =
            savedStage === 1
              ? 1
              : savedStage <= 6
                ? savedStage - 1
                : savedStage;
        } else if (savedJourneyVersion === 3 && savedStage <= 3) {
          savedStage = 1;
        }
        // Pre-v7 drafts had Salary Slip at stage 5; it was removed, so later
        // stages shift down by one and a draft on Salary Slip resumes at Selfie.
        if (savedJourneyVersion !== 7 && savedStage >= 6) savedStage -= 1;
        savedStage = Math.min(SUBMIT_STEP + 1, savedStage);

        // A delayed stage save must not send applicants back through completed
        // verification. Reconcile against live records, not cached done flags.
        if (savedStage >= 3 && savedStage <= 5) {
          const readSaved = async (path) => {
            const response = await fetch(`${API}${path}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const result = await response.json();
            if (!response.ok || !result.success)
              throw new Error("Unable to check saved verification");
            return result;
          };
          const [addressProofResult, selfieResult] = await Promise.allSettled([
            readSaved("/api/profile/address-proof"),
            readSaved("/api/profile/selfie"),
          ]);
          if (!active) return;
          savedData.addressProofDone = !savedData.addressMismatch;
          for (const [prefix, result] of [
            ["addressProof", addressProofResult],
          ]) {
            if (result.status !== "fulfilled") continue;
            if (result.value.required === false) {
              savedData[`${prefix}NotRequired`] = true;
              savedData[`${prefix}Done`] = true;
              continue;
            }
            if (result.value.reusable !== true) continue;
            savedData[`${prefix}Done`] = true;
            savedData[`${prefix}DocumentId`] = result.value.documentId;
            savedData[`${prefix}FileName`] = result.value.fileName;
            savedData[`${prefix}UploadedAt`] = result.value.uploadedAt;
          }
          if (
            selfieResult.status === "fulfilled" &&
            selfieResult.value.reusable === true &&
            selfieResult.value.imageUrl
          ) {
            savedData.selfieDone = true;
            savedData.selfieUploaded = true;
            savedData.selfieImage = selfieResult.value.imageUrl;
            savedData.selfieCapturedAt = selfieResult.value.uploadedAt;
          }
          savedStage = advanceCompletedVerificationStages(
            savedStage,
            savedData,
            7,
          );
        }

        // A below-minimum credit score (or blocked NTC) keeps the user on Employment Info.
        if (json.creditCheck?.eligible === false) savedStage = 1;

        // A draft saved without an amount must not wipe the step 0 default.
        if (!(Number(savedData.amount) > 0)) delete savedData.amount;

        journeyHydratedRef.current = true;
        setData((prev) => ({ ...prev, ...savedData }));
        setStep(savedStage - 1);
      } catch (error) {
        console.error("Failed to load journey data:", error);
        if (active)
          setJourneyLoadError(
            error.message || "Unable to load your saved application.",
          );
      } finally {
        if (active) setJourneyReady(true);
      }
    };

    loadJourneyData();
    return () => {
      active = false;
    };
  }, [navigate, resetJourneyForNewApplication, token, userId]);

  // On mount: fetch profile to set kycDone even when StepKycHandoff is not mounted
  useEffect(() => {
    if (!userId) return;
    let active = true;
    (async () => {
      try {
        const response = await fetch(`${API}/api/auth/profile/${userId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        const json = await response.json();
        if (!active || !response.ok || !json.success) return;
        const kycStatusLocal = String(
          json.user?.kycStatus || json.user?.kyc_status || "pending",
        ).toLowerCase();
        const aadhaarRecord = json.kycRecords?.aadhaar || {};
        const aadhaarStatus = String(aadhaarRecord.status || "").toUpperCase();
        const aadhaarVerified =
          !!(
            aadhaarRecord.name ||
            aadhaarRecord.aadhaar_name ||
            aadhaarRecord.uid
          ) || aadhaarStatus === "COMPLETED";
        let finalStatus = kycStatusLocal;
        if (finalStatus === "pending" && aadhaarVerified) {
          finalStatus = "under_review";
        }
        const isDone = ["completed", "verified", "under_review"].includes(
          finalStatus,
        );
        setData((prev) => ({
          ...prev,
          kycDone: isDone,
          kycStatus: finalStatus,
          phoneMismatch: aadhaarRecord.phoneMismatch === true,
          aadhaarMismatch: aadhaarRecord.aadhaarMismatch === true,
          addressMismatch: aadhaarRecord.addressMismatch !== false,
        }));
      } catch {
        // best-effort only
      }
    })();
    return () => {
      active = false;
    };
  }, [userId, token]);

  const cur = STEPS[step];

  // Stage progress lives in the dashboard header instead of an in-page card.
  const setHeaderStage = useOutletContext()?.setHeaderStage;
  useEffect(() => {
    setHeaderStage?.({
      step: step + 1,
      total: SUBMIT_STEP + 1,
      icon: cur.icon,
      title: cur.title,
      desc: cur.desc,
    });
  }, [setHeaderStage, step, cur]);
  useEffect(() => () => setHeaderStage?.(null), [setHeaderStage]);
  const accentRgb = hexToRgb(cur.color);
  const relationsValidationMessage = getRelationsValidationMessage(data);
  const isRelationsStepValid = !relationsValidationMessage;
  const employmentValidationMessage = getEmploymentValidationMessage(data);
  const isEmploymentStepValid = !employmentValidationMessage;
  const set = useCallback((k, v) => {
    const invalidatingKeys = new Set([
      "amount",
      "tenure",
      "empType",
      "employer",
      "income",
      "designation",
      "businessType",
      "industry",
      "workType",
    ]);

    setData((p) => {
      const next = { ...p, [k]: v };
      if (invalidatingKeys.has(k)) {
        next.bankDone = false;
        next.bankAccountNumber = "";
        next.bankIfscCode = "";
        next.esignDone = false;
        next.borrowerSignature = "";
        next.esignSignedAt = null;
      }
      return next;
    });
    setStepError("");
  }, []);

  const stageLeftRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    // On laptops the form scrolls inside the card, not the window.
    stageLeftRef.current?.scrollTo({ top: 0 });
  }, [step]);

  useEffect(() => {
    if (
      !userId ||
      !journeyReady ||
      journeyLoadError ||
      applicationBlocked ||
      sanctionUnlocked ||
      isSubmitting ||
      submittedApplicationRef.current ||
      submitted
    )
      return undefined;

    // Synchronously save to local storage
    localStorage.setItem(`loan_journey_draft_${userId}`, JSON.stringify(data));
    localStorage.setItem(
      `loan_journey_checkpoint_${userId}`,
      JSON.stringify({
        currentStage: step + 1,
        journeyData: { ...data, journeyVersion: 7 },
        savedAt: new Date().toISOString(),
      }),
    );

    const timer = window.setTimeout(
      () => {
        autosaveRef.current = (async () => {
          if (
            !journeyReady ||
            !userId ||
            applicationBlocked ||
            isSubmitting ||
            submitted
          )
            return;
          try {
            const response = await fetch(`${API}/api/loan-progress/save`, {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({
                currentStage: step + 1,
                status: submitted ? "COMPLETED" : "IN_PROGRESS",
                journeyData: { ...data, journeyVersion: 7 },
              }),
            });
            if (!response.ok) {
              const result = await response.json().catch(() => ({}));
              // Shown on the email field and as a toast, not in the save banner.
              if (result.code === "EMAIL_ALREADY_EXISTS") {
                const takenEmail = data.email;
                setData((prev) =>
                  prev.emailTaken === takenEmail
                    ? prev
                    : { ...prev, emailTaken: takenEmail },
                );
                setSaveError("");
                return;
              }
              throw new Error(
                result.message ||
                  `Unable to save progress (${response.status})`,
              );
            }
            setSaveError("");
          } catch (error) {
            setSaveError(error.message);
            console.error("Failed to auto-save journey:", error);
          } finally {
            journeyHydratedRef.current = true;
          }
        })();
      },
      journeyHydratedRef.current ? 700 : 0,
    );

    return () => window.clearTimeout(timer);
  }, [
    applicationBlocked,
    data,
    isSubmitting,
    journeyReady,
    journeyLoadError,
    sanctionUnlocked,
    step,
    submitted,
    token,
    userId,
  ]);

  const completeAccountAggregator = useCallback(() => {
    setStepError("");
    setStep((previous) =>
      previous === 1 ? (data.returnToSalaryAccount ? 6 : 2) : previous,
    );
    if (data.returnToSalaryAccount)
      setData((previous) => ({ ...previous, returnToSalaryAccount: false }));
  }, [data.returnToSalaryAccount]);

  const creditBlockedMessage =
    creditCheck?.eligible === false
      ? creditCheck.message ||
        `Your credit score (${creditCheck.score}) is below the minimum of ${creditCheck.minScore} required for a loan, so you can't continue with this application.`
      : "";

  const next = async () => {
    if (savingStep) return;
    if (step === 0 && creditBlockedMessage) {
      setStepError(creditBlockedMessage);
      return;
    }

    if (step === 0 && !isEmploymentStepValid) {
      setStepError(employmentValidationMessage);
      return;
    }

    const isSelfEmployed = step === 0 && data.empType === "Self-Employed";

    // Leaving Employment Info: save first so the backend can check the credit score.
    if (step === 0) {
      setSavingStep(true);
      try {
        const response = await fetch(`${API}/api/loan-progress/save`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            // Self-employed applicants do not move past Employment Info.
            currentStage: isSelfEmployed ? 1 : 2,
            status: "IN_PROGRESS",
            journeyData: { ...data, journeyVersion: 7 },
          }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.success) {
          if (result.code === "CREDIT_SCORE_TOO_LOW" && result.creditCheck) {
            setCreditCheck(result.creditCheck);
          }
          if (result.code === "EMAIL_ALREADY_EXISTS") {
            setData((prev) => ({ ...prev, emailTaken: data.email }));
          }
          if (result.code === "KYC_INCOMPLETE") {
            setData((prev) => ({
              ...prev,
              kycDone: false,
              kycStatus: result.kycStatus || prev.kycStatus,
              phoneMismatch: result.phoneMismatch === true,
              aadhaarMismatch: result.aadhaarMismatch === true,
            }));
            setStep(2);
          }
          setStepError(
            result.message || "Unable to save your details. Please try again.",
          );
          return;
        }
      } catch {
        setStepError("Unable to reach the server. Please try again.");
        return;
      } finally {
        setSavingStep(false);
      }
    }

    if (isSelfEmployed) {
      setStepError("");
      setSelfEmployedThanks(true);
      return;
    }

    if (step === 1 && !data.aaDone) {
      setStepError(
        "Please connect your bank account or upload a statement to proceed.",
      );
      return;
    }

    if (step === 2 && data.aadhaarMismatch) {
      setStepError(AADHAAR_MISMATCH_MESSAGE);
      return;
    }
    if (step === 2 && data.phoneMismatch) {
      setStepError(PHONE_MISMATCH_MESSAGE);
      return;
    }

    if (step === 2 && !data.kycDone) {
      setStepError(
        "Please complete your Aadhaar KYC verification before proceeding.",
      );
      return;
    }

    if (step === 3 && !data.addressProofDone) {
      setStepError("Please upload your address proof to proceed.");
      return;
    }

    if (step === 4 && !data.selfieDone) {
      setStepError("Please complete the selfie verification to proceed.");
      return;
    }

    if (step === 5 && !isRelationsStepValid) {
      setStepError(relationsValidationMessage);
      return;
    }

    if (step === 6 && !data.salaryAccountDone) {
      setStepError(
        "Please select your salary account and enter its full account number to verify it.",
      );
      return;
    }

    setStepError("");
    if (step < SUBMIT_STEP) {
      setStep((p) => p + 1);
    } else {
      setSubmitted(true);
    }
  };

  useEffect(() => {
    if (!submitted) return undefined;
    let cancelled = false;
    fetch(`${API}/api/lead/status`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((response) => response.json())
      .then((json) => {
        if (!cancelled && json.success) setLeadStatus(json);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [submitted, token]);

  const handleFinalSubmit = useCallback(async () => {
    if (isSubmitting) return;
    if (!data.salaryAccountDone) {
      setSubmitError("Please select and save your salary account.");
      return;
    }
    if (!userId) {
      setSubmitError("Session expired. Please login again.");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    try {
      await autosaveRef.current;
      let json = submittedApplicationRef.current;
      if (!json) {
        const response = await fetch(`${API}/api/loans/apply`, {
          method: "POST",
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        json = await response.json().catch(() => ({}));
        if (!response.ok || !json.success) {
          throw new Error(json.message || "Failed to submit application");
        }
        submittedApplicationRef.current = json;
      }

      // Hand the lead to the AI agent; on retry, skip it if it already went through.
      if (!agentSubmittedRef.current) {
        const agentResponse = await fetch(`${API}/api/post/ai/agent`, {
          method: "POST",
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const agentJson = await agentResponse.json().catch(() => ({}));
        if (!agentResponse.ok || !agentJson.success) {
          throw new Error(
            agentJson.message ||
              "Could not send your application to our agent. Please retry Save & Submit.",
          );
        }
        agentSubmittedRef.current = true;
      }

      setData((prev) => ({
        ...prev,
        leadId: json.leadId,
        loanId: json.loanId || null,
        loanApplied: true,
      }));

      const saveResponse = await fetch(`${API}/api/loan-progress/save`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          currentStage: SUBMIT_STEP + 1,
          status: "COMPLETED",
          loanId: json.loanId || null,
          journeyData: {
            ...data,
            leadId: json.leadId,
            loanId: json.loanId || null,
            loanApplied: true,
          },
        }),
      });

      const saved = await saveResponse.json().catch(() => ({}));
      if (!saveResponse.ok || !saved.success) {
        throw new Error(
          saved.message ||
            "Application submitted, but completion could not be saved. Please retry Save & Submit.",
        );
      }
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || "Failed to complete journey");
    } finally {
      setIsSubmitting(false);
    }
  }, [data, isSubmitting, token, userId]);

  if (!journeyReady || journeyLoadError) {
    return (
      <div className="alp-root alp-compact" role="status">
        <p>{journeyLoadError || "Restoring your application…"}</p>
        {journeyLoadError && (
          <button
            className="alp-btn-primary"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  if (applicationBlocked) {
    const blockedLeadStatus = String(
      blockedEligibility?.leadStatus || "",
    ).toUpperCase();
    const blockedLoanStatus = String(
      blockedEligibility?.loanStatus || "",
    ).toUpperCase();
    const agentName = blockedEligibility?.latestLead?.ai_agent_name;
    const hasOpenLoan = !!blockedLoanStatus && blockedLoanStatus !== "CLOSED";
    let blockedMessage =
      "Your application is being processed. You can check its status on your dashboard.";
    let showMyLoans = false;
    if (hasOpenLoan || blockedLeadStatus === "DISBURSED") {
      blockedMessage =
        "Your loan has been disbursed and is not yet repaid. Go to My Loans to view and repay it.";
      showMyLoans = true;
    } else if (blockedLeadStatus === "WITH_AGENT") {
      blockedMessage = agentName
        ? `Your application is with ${agentName}, our agent.`
        : "Your application is with our agent.";
    } else if (
      blockedLeadStatus === "REJECTED" ||
      blockedLeadStatus === "AGENT_REJECTED"
    ) {
      blockedMessage = "Your application is rejected.";
    }
    const isRejected =
      blockedLeadStatus === "REJECTED" ||
      blockedLeadStatus === "AGENT_REJECTED";
    const blockedView = showMyLoans
      ? {
          tone: "active",
          icon: "💸",
          badge: "Loan Active",
          title: "Your loan is active",
          current: 4,
        }
      : isRejected
        ? {
            tone: "rejected",
            icon: "📋",
            badge: "Not Approved",
            title: "Application not approved",
            current: 2,
          }
        : blockedLeadStatus === "WITH_AGENT"
          ? {
              tone: "agent",
              icon: "🧑‍💼",
              badge: "With Agent",
              title: "Your application is under review",
              current: 1,
            }
          : {
              tone: "processing",
              icon: "⏳",
              badge: "In Process",
              title: "Your application is in progress",
              current: 1,
            };
    const blockedSteps = ["Applied", "Under Review", "Decision", "Disbursal"];
    return (
      <div className="alp-root alp-compact">
        <section className={`alp-blocked alp-blocked-${blockedView.tone}`}>
          <div className="alp-blocked-bg" aria-hidden="true">
            <span className="alp-blocked-chip c1">₹</span>
            <span className="alp-blocked-chip c2">📄</span>
            <span className="alp-blocked-chip c3">%</span>
          </div>

          <div className="alp-blocked-visual" aria-hidden="true">
            <span className="alp-blocked-ring" />
            <span className="alp-blocked-ring alp-blocked-ring-2" />
            <span className="alp-blocked-icon">{blockedView.icon}</span>
          </div>

          <span className="alp-blocked-badge">
            <i aria-hidden="true" />
            {blockedView.badge}
          </span>
          <h2 className="alp-blocked-title">{blockedView.title}</h2>
          <p className="alp-blocked-message">{blockedMessage}</p>

          <ol className="alp-blocked-track" aria-label="Application progress">
            {blockedSteps.map((label, index) => {
              const state =
                isRejected && index === blockedView.current
                  ? "failed"
                  : index < blockedView.current
                    ? "done"
                    : index === blockedView.current
                      ? "current"
                      : "pending";
              return (
                <li key={label} className={`is-${state}`}>
                  <span className="alp-blocked-dot">
                    {state === "done"
                      ? "✓"
                      : state === "failed"
                        ? "✕"
                        : index + 1}
                  </span>
                  <span className="alp-blocked-step">{label}</span>
                </li>
              );
            })}
          </ol>

          <div className="alp-blocked-actions">
            {showMyLoans ? (
              <button
                className="alp-btn-primary"
                onClick={() => navigate("/dashboard/loans")}
              >
                Go to My Loans
              </button>
            ) : (
              <button
                className="alp-btn-ghost"
                onClick={() => navigate("/dashboard")}
              >
                Dashboard
              </button>
            )}
          </div>
          <p className="alp-blocked-help">
            Have a question?{" "}
            <button
              type="button"
              onClick={() => navigate("/dashboard/support")}
            >
              Contact Support
            </button>
          </p>
        </section>
      </div>
    );
  }

  if (approvedOffer) {
    const approvedAmount = Number(
      approvedOffer.recommended_loan_amount ||
        approvedOffer.sanctioned_amount ||
        approvedOffer.approved_amount ||
        approvedOffer.loan_amount ||
        0,
    );
    const formattedAmount =
      approvedAmount > 0
        ? new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 0,
          }).format(approvedAmount)
        : "your approved amount";
    const offerSteps = [
      "Application accepted",
      "Offer generated",
      "Aadhaar eSign",
    ];

    return (
      <div className="alp-root alp-compact">
        <section
          className="alp-blocked alp-blocked-approved"
          aria-labelledby="approved-offer-title"
        >
          <div className="alp-blocked-bg" aria-hidden="true">
            <span className="alp-blocked-chip c1">₹</span>
            <span className="alp-blocked-chip c2">📄</span>
            <span className="alp-blocked-chip c3">✓</span>
          </div>

          <div className="alp-blocked-visual" aria-hidden="true">
            <span className="alp-blocked-ring" />
            <span className="alp-blocked-ring alp-blocked-ring-2" />
            <span className="alp-blocked-icon">🎉</span>
          </div>

          <span className="alp-blocked-badge">
            <i aria-hidden="true" />
            Offer ready
          </span>
          <h2 id="approved-offer-title" className="alp-blocked-title">
            Your application has been accepted
          </h2>
          <p className="alp-blocked-message">
            Your loan offer of <strong>{formattedAmount}</strong> has been
            generated. Review your sanction letter and proceed with Aadhaar
            eSign to accept the offer.
          </p>

          <ol className="alp-blocked-track" aria-label="Application progress">
            {offerSteps.map((label, index) => {
              const state = index < 2 ? "done" : "current";
              return (
                <li key={label} className={`is-${state}`}>
                  <span className="alp-blocked-dot">
                    {state === "done" ? "✓" : index + 1}
                  </span>
                  <span className="alp-blocked-step">{label}</span>
                </li>
              );
            })}
          </ol>

          <div className="alp-blocked-actions">
            <button
              className="alp-btn-primary"
              onClick={() => navigate("/dashboard/sanction-letter")}
            >
              Proceed to eSign
            </button>
          </div>
          <p className="alp-blocked-help">
            Please review the sanction letter carefully before signing.
          </p>
        </section>
      </div>
    );
  }

  if (selfEmployedThanks)
    return (
      <div
        className="alp-root alp-compact"
        style={{ "--alp-accent": cur.color, "--alp-accent-rgb": accentRgb }}
      >
        <div className="alp-success-screen">
          <div className="alp-success-box">
            <div className="alp-success-burst">🙏</div>
            <h1>Thanks for applying</h1>
            <p>
              We have received your details. Our team will get back to you soon.
            </p>
            <div
              style={{
                display: "flex",
                gap: "0.75rem",
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <button
                className="alp-btn-ghost"
                onClick={() => navigate("/dashboard")}
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );

  if (isSubmitting)
    return (
      <div
        className="alp-root alp-compact"
        style={{ "--alp-accent": cur.color, "--alp-accent-rgb": accentRgb }}
      >
        <div className="alp-success-screen" role="status" aria-live="polite">
          <div className="alp-success-box">
            <div className="alp-submit-spinner" aria-hidden="true" />
            <h1>Submitting your application</h1>
            <p>
              Sending your details to our agent. Please don't close this page.
            </p>
          </div>
        </div>
      </div>
    );

  if (submitted)
    return (
      <div
        className="alp-root alp-compact"
        style={{ "--alp-accent": cur.color, "--alp-accent-rgb": accentRgb }}
      >
        <div className="alp-success-screen">
          <div className="alp-success-box">
            <div className="alp-success-burst">🎉</div>
            <h1>Your lead is with our Review Team</h1>
            <p>
              {leadStatus?.ai_agent_name
                ? `${leadStatus.ai_agent_name}, our agent, is reviewing your application.`
                : "Our agent is reviewing your application."}{" "}
              You can check its status on your dashboard.
            </p>
            <div
              style={{
                display: "flex",
                gap: "0.75rem",
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <button
                className="alp-btn-ghost"
                onClick={() => navigate("/dashboard/loans")}
              >
                View My Loans
              </button>
              <button
                className="alp-btn-ghost"
                onClick={() => navigate("/dashboard")}
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );

  // Why Continue is disabled on this step; "" when it can be pressed. The
  // location gate adds its own reason on step 0.
  const stepBlockedReason =
    step === 0 && creditBlockedMessage
      ? "You cannot continue because of the credit check result above."
      : step === 1 && !data.aaDone
        ? "Complete the bank verification to continue."
        : step === 2 && (data.phoneMismatch || data.aadhaarMismatch)
          ? "Complete KYC with the correct Aadhaar to continue."
          : step === 2 && !data.kycDone
            ? "Complete your Aadhaar KYC to continue."
            : step === 3 && !data.addressProofDone
              ? "Upload your address proof to continue."
              : step === 4 && !data.selfieDone
                ? "Capture your selfie to continue."
                : step === 5 && !isRelationsStepValid
                  ? relationsValidationMessage
                  : step === 6 && !data.salaryAccountDone
                    ? "Select and save your salary account to continue."
                    : "";

  return (
    <LocationGate enabled={journeyReady && step === 0}>
      {({
        blocked: locationBlocked,
        prompt: locationPrompt,
        reason: locationReason,
      }) => (
        <div
          className="alp-root alp-compact alp-fit"
          style={{ "--alp-accent": cur.color, "--alp-accent-rgb": accentRgb }}
        >
          <main className="alp-main alp-split">
            <div className="alp-split-form">
              <div className="alp-stage-card">
                <div className="alp-stage-left" ref={stageLeftRef}>
                  {step === 0 && locationPrompt}
                  {step === 0 && (
                    <StepEmployment
                      data={data}
                      onChange={set}
                      validationMessage={stepError}
                      blockMessage={creditBlockedMessage}
                    />
                  )}
                  {step === 1 && (
                    <StepAccountAggregator
                      data={data}
                      onChange={set}
                      onComplete={completeAccountAggregator}
                    />
                  )}
                  {step === 2 && <StepKycHandoff data={data} onChange={set} />}
                  {step === 3 && (
                    <StepAddressProof data={data} onChange={set} />
                  )}
                  {step === 4 && (
                    <StepSelfieCapture data={data} onChange={set} />
                  )}
                  {step === 5 && (
                    <StepRelations
                      data={data}
                      onChange={set}
                      validationMessage={stepError}
                    />
                  )}
                  {step === 6 && (
                    <SalaryAccountStep
                      data={data}
                      onChange={set}
                      onRestartAA={() => {
                        setData((previous) => ({
                          ...previous,
                          salaryAccountDone: false,
                          returnToSalaryAccount: true,
                        }));
                        setStep(1);
                      }}
                    />
                  )}
                </div>
                <ApplyStageShowcase stepKey={cur.key} icon={cur.icon} />
              </div>
              {saveError && (
                <div className="alp-alert alp-aa-error" role="alert">
                  Progress could not be saved: {saveError}
                </div>
              )}
              {submitError &&
                (alreadySubmittedError ? (
                  <div className="alp-submitted-lock">
                    <div className="alp-submitted-art" aria-hidden="true">
                      📮
                    </div>
                    <div className="alp-submitted-copy">
                      <div className="alp-submitted-kicker">
                        Application Status
                      </div>
                      <h3>Loan Already Submitted</h3>
                      <p>
                        Your previous loan application is already in
                        review/pending/active state. New application will be
                        available after completion.
                      </p>
                    </div>
                    <div className="alp-submitted-actions">
                      <button
                        className="alp-btn-ghost"
                        onClick={() => navigate("/dashboard/loans")}
                      >
                        View My Loans
                      </button>
                      <button
                        className="alp-btn-ghost"
                        onClick={() => navigate("/dashboard")}
                      >
                        Go to Dashboard
                      </button>
                    </div>
                  </div>
                ) : (
                  <AlertToast title="Error" message={submitError} />
                ))}
              <div className="alp-nav">
                {step > 0 && (
                  <button
                    className="alp-btn-ghost"
                    onClick={() => setStep((p) => p - 1)}
                  >
                    Back
                  </button>
                )}
                {!alreadySubmittedError && (
                  <button
                    className="alp-btn-primary"
                    title={
                      (locationBlocked ? locationReason : stepBlockedReason) ||
                      undefined
                    }
                    onClick={() => {
                      if (!locationBlocked) {
                        if (step === SUBMIT_STEP) handleFinalSubmit();
                        else next();
                      }
                    }}
                    disabled={
                      isSubmitting ||
                      savingStep ||
                      locationBlocked ||
                      !!stepBlockedReason
                    }
                  >
                    {isSubmitting
                      ? "Submitting…"
                      : savingStep
                        ? "Saving…"
                        : step === SUBMIT_STEP
                          ? "Save & Submit"
                          : "Continue"}
                  </button>
                )}
              </div>
              {!alreadySubmittedError &&
                !isSubmitting &&
                !savingStep &&
                (locationBlocked || stepBlockedReason) && (
                  <div className="alp-nav-reason" role="status">
                    <span aria-hidden="true">🔒</span>{" "}
                    {locationBlocked ? locationReason : stepBlockedReason}
                  </div>
                )}
            </div>
            {/* <footer className="alp-page-footer">
              <div className="alp-page-footer-brand">
                <span aria-hidden="true">🛡️</span>
                <div>
                  <strong>SafeTensor</strong>
                  <small>Trusted by thousands for a safer tomorrow.</small>
                </div>
              </div>
              <div className="alp-page-footer-tags">
                <span>🔒 Secure</span>
                <span>Simple</span>
                <span>Transparent</span>
              </div>
            </footer> */}
          </main>
        </div>
      )}
    </LocationGate>
  );
}
